"use client";

import { NativeLink as Link } from "@/components/native-link";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject, type ReactNode, type SyntheticEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Download, Flag, HelpCircle, MessageSquareText, MoreHorizontal, Pause, Play, RefreshCw, SkipBack, SkipForward, Trash2, Video, VideoOff, Volume2, VolumeX } from "lucide-react";
import { Card, Pill } from "@/components/pulse-shell";
import { SessionDetailToolbar } from "@/components/session-detail-toolbar";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PipelineTimelineModal } from "@/components/pipeline-timeline-modal";
import { PerceptionChannelsStrip } from "@/components/perception-channels";
import { api, ApiError, type SessionDetail } from "@/lib/api";
import { useAuthProfile, useRoleImpersonation } from "@/lib/auth-context";
import {
  avatarGradientForUserKey,
  displayTopic,
  displayUserName,
  formatEndReason,
  initialsFrom,
  moodLabel,
  moodMomentChipStyle,
  sessionUserAvatarKey,
  toDisplayStatus,
} from "@/lib/sessions";
import { downloadSessionReportPdf } from "@/lib/session-report-pdf";
import { isHiddenByRoleScope } from "@/lib/session-role-scope";
import {
  archiveObjectIdentity,
  clampMediaTimeToBuffered,
  sameArchiveObject,
  stabilizeSessionDetailMedia,
} from "@/lib/session-media-urls";
import { hasPermission } from "@/lib/users-rbac";
import {
  isSttUnrecognized,
  STT_UNRECOGNIZED_LABEL,
  sttUnrecognizedDisplayText,
} from "@/lib/stt-labels";
import {
  archivePeaksPending,
  computeArchivePlaybackState,
} from "@/lib/session-archive-playback";
import { buildVideoModalContext } from "@/lib/session-video-context";
import { insightBulletsBeyondBody, parseObservationFact } from "@/lib/perception-insight-display";

function perceptionPassInFlight(status?: string | null) {
  const st = String(status || "").toLowerCase();
  return st === "pending_video" || st === "running";
}

function time(value: number) {
  const totalMs = Math.max(0, Math.round(value * 1000));
  const m = Math.floor(totalMs / 60000);
  const s = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  if (ms === 0) {
    return `${m}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`;
}

/** HTMLMediaElement rate change without chipmunk pitch (Web Audio buffer sources cannot do this). */
function applyMediaPlaybackRate(el: HTMLMediaElement, rate: number) {
  const r = Math.max(0.25, Math.min(4, rate));
  el.playbackRate = r;
  try {
    (el as HTMLMediaElement & { preservesPitch?: boolean }).preservesPitch = true;
    (el as unknown as { mozPreservesPitch?: boolean }).mozPreservesPitch = true;
    (el as unknown as { webkitPreservesPitch?: boolean }).webkitPreservesPitch = true;
  } catch {
    /* legacy */
  }
}

/** True when the media element already points at `url` (attr or absolute src). */
function mediaPointsAt(el: HTMLMediaElement, url: string): boolean {
  if (!url) return false;
  const attr = el.getAttribute("src") || "";
  if (attr === url) return true;
  try {
    return el.src === url || el.src === new URL(url, window.location.href).href;
  } catch {
    return el.src === url;
  }
}

/** Bar height caps — must fit `.wave-lane` (124px single, 56px per lane in duo). */
const WAVEFORM_BAR_MAX_SINGLE = 118;
const WAVEFORM_BAR_MAX_DUO_LANE = 52;

function prepareWaveform(
  peaks: number[] | null | undefined,
  targetBars = 300,
  maxHeight = WAVEFORM_BAR_MAX_SINGLE,
  opts?: { floor?: number },
): number[] {
  if (!peaks?.length) return [];
  const floor = opts?.floor ?? 10;
  const src = peaks.map((h) => Number(h) || 0);
  // All-zero bot lane (decode miss / silence) → flat empty bars.
  if (src.every((h) => h <= 0)) {
    return Array.from({ length: targetBars }, () => Math.max(2, Math.round(maxHeight * 0.08)));
  }
  let densified = src;
  if (src.length < targetBars) {
    densified = Array.from({ length: targetBars }, (_, i) => {
      const pos = (i / Math.max(targetBars - 1, 1)) * (src.length - 1);
      const i0 = Math.floor(pos);
      const i1 = Math.min(src.length - 1, i0 + 1);
      const t = pos - i0;
      return src[i0] * (1 - t) + src[i1] * t;
    });
  } else if (src.length > targetBars * 1.35) {
    // Mild downsample when postprocess already produced a very dense series
    const step = src.length / targetBars;
    densified = Array.from({ length: targetBars }, (_, i) => {
      const start = Math.floor(i * step);
      const end = Math.max(start + 1, Math.floor((i + 1) * step));
      let sum = 0;
      for (let j = start; j < end && j < src.length; j += 1) sum += src[j];
      return sum / Math.max(1, end - start);
    });
  }
  const max = Math.max(...densified, 1);
  const min = Math.min(...densified);
  const span = Math.max(max - min, 1);
  return densified.map((h) => {
    const norm = (h - min) / span;
    // Mild contrast so quiet/loud regions stay readable at taller scale
    const shaped = Math.pow(norm, 0.78);
    return Math.max(floor, Math.round(floor + 2 + shaped * (maxHeight - floor - 2)));
  });
}

/** Shift archive-relative peaks onto the session clock using trackOffsetsSec. */
function shiftPeaksForClock(
  peaks: number[] | null | undefined,
  offsetSec: number,
  durationSec: number,
): number[] | null {
  if (!peaks?.length) return null;
  const n = peaks.length;
  const shift = Math.round((Math.max(0, offsetSec) / Math.max(durationSec, 1)) * n);
  if (shift <= 0) return peaks;
  const out = Array.from({ length: n }, () => 0);
  for (let i = 0; i < n - shift; i += 1) out[i + shift] = peaks[i];
  return out;
}

function capturePortrait(video: HTMLVideoElement): string | null {
  if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) return null;
  const canvas = document.createElement("canvas");
  const size = 128;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const side = Math.min(video.videoWidth, video.videoHeight);
  ctx.drawImage(
    video,
    (video.videoWidth - side) / 2,
    (video.videoHeight - side) / 2,
    side,
    side,
    0,
    0,
    size,
    size,
  );
  try {
    return canvas.toDataURL("image/jpeg", 0.82);
  } catch {
    return null;
  }
}

type Seg = { audioUrl: string; startMs: number; endMs: number; role?: string; text?: string };

function buildPlaylist(detail: SessionDetail | null): Seg[] {
  if (!detail) return [];
  const fromTurns = (detail.turns || [])
    .filter((t) => t.audioUrl)
    .map((t) => {
      const startMs = Number(t.startMs || 0);
      const durationMs = Number(t.durationMs || Math.max(0, Number(t.endMs || 0) - startMs) || 800);
      return {
        audioUrl: String(t.audioUrl),
        startMs,
        endMs: startMs + durationMs,
        role: t.role,
        text: t.text,
      };
    });
  if (fromTurns.length) return fromTurns.sort((a, b) => a.startMs - b.startMs);
  return (detail.segmentAudioUrls || [])
    .filter((s) => s.audioUrl)
    .map((s) => {
      const startMs = Number(s.startMs || 0);
      return { audioUrl: String(s.audioUrl), startMs, endMs: startMs + 1000, role: s.role, text: s.text };
    })
    .sort((a, b) => a.startMs - b.startMs);
}

function affectScore(labels: string[]): { engagement: number; valence: number; arousal: number } {
  if (!labels.length) return { engagement: 55, valence: 50, arousal: 40 };
  const pos = new Set(["joy", "amusement", "surprise"]);
  const neg = new Set(["sadness", "anger", "frustration", "fear"]);
  let v = 50;
  let a = 40;
  for (const lab of labels) {
    const s = lab.toLowerCase();
    if (pos.has(s)) { v += 8; a += 6; }
    else if (neg.has(s)) { v -= 8; a += 8; }
    else { a += 2; }
  }
  return {
    engagement: Math.max(35, Math.min(96, 50 + labels.length * 4)),
    valence: Math.max(15, Math.min(95, v)),
    arousal: Math.max(20, Math.min(92, a)),
  };
}

/** Heuristic 0–100: higher = more communication friction in the session. */
function communicationIssuesScore(detail: SessionDetail | null, affectLabels: string[]): number {
  if (!detail) return 0;
  let score = 0;
  const reason = String(detail.statusReason || detail.abandonReason || "").toLowerCase();
  if (reason.includes("upload") || reason.includes("failed") || reason.includes("error") || reason.includes("incomplete")) {
    score += 18;
  }
  const frictionAffect = new Set(["frustration", "anger", "confusion", "fear", "sadness"]);
  for (const lab of affectLabels) {
    if (frictionAffect.has(lab.toLowerCase())) score += 10;
  }
  for (const m of detail.keyMoments || []) {
    const lab = String(m.label || "").toLowerCase();
    if (/interrupt|unclear|repeat|mishear|silence|dropout|latency|friction|confused/.test(lab)) score += 8;
  }
  const texts = [
    ...(detail.transcripts || []).map((t) => t.text || ""),
    ...(detail.turns || []).map((t) => t.text || ""),
  ].join("\n").toLowerCase();
  const phrases = [
    "can you hear", "say again", "repeat", "didn't hear", "did not hear", "what did you say",
    "you're cutting", "cutting out", "speak up", "slow down", "i can't hear", "sorry what",
  ];
  for (const p of phrases) {
    if (texts.includes(p)) score += 7;
  }
  return Math.max(0, Math.min(100, score));
}

const RATES = [0.75, 1, 1.25, 1.5] as const;

const SESSION_SIGNAL_HELP = {
  moodScore:
    "Your headline read on the call, from 0 to 100. Higher means a smoother, more engaged conversation.",
  moodLabel:
    "The mood at the moment you are reviewing. Use the chips under the timeline to jump to other highlights.",
  moodLabelsTotal:
    "How often the tone noticeably shifted—for example from calm to frustrated and back.",
  engagement:
    "How present and involved the participant seemed throughout the session.",
  valence:
    "Whether the overall tone felt warm and constructive or tense and negative.",
  arousal:
    "How calm versus energized the emotional tone felt across the call.",
  comfort:
    "How natural and at-ease the conversation felt—combining engagement with a positive tone.",
  communicationIssues:
    "Signals that something may have disrupted the flow, such as frustration, confusion, silence, or connection trouble.",
} as const;

type SessionMetricHelpSection = { title: string; body: string };

function SessionMetricHelpPopover({
  ariaLabel,
  dialogTitle,
  sections,
  wrapClassName = "",
  btnClassName = "",
  iconSize = 15,
}: {
  ariaLabel: string;
  dialogTitle: string;
  sections: readonly SessionMetricHelpSection[];
  wrapClassName?: string;
  btnClassName?: string;
  iconSize?: number;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const place = () => {
      const btn = btnRef.current;
      const pop = popRef.current;
      if (!btn || !pop) return;
      const br = btn.getBoundingClientRect();
      const pad = 8;
      const gap = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pw = Math.min(360, Math.max(280, pop.offsetWidth || 300));
      const preferBelow = vh - br.bottom - gap - pad;
      const preferAbove = br.top - gap - pad;
      const placeBelow = preferBelow >= 120 || preferBelow >= preferAbove;
      const maxHeight = Math.max(120, Math.min(placeBelow ? preferBelow : preferAbove, vh * 0.75));
      pop.style.maxHeight = `${maxHeight}px`;
      const ph = pop.offsetHeight || Math.min(280, maxHeight);
      let left = br.right - pw;
      if (left + pw > vw - pad) left = vw - pad - pw;
      if (left < pad) left = pad;
      let top = placeBelow ? br.bottom + gap : br.top - gap - ph;
      if (top < pad) top = pad;
      if (top + ph > vh - pad) top = Math.max(pad, vh - pad - ph);
      setPos((prev) => {
        if (
          prev
          && Math.abs(prev.top - top) < 0.5
          && Math.abs(prev.left - left) < 0.5
          && prev.maxHeight === maxHeight
        ) {
          return prev;
        }
        return { top, left, maxHeight };
      });
    };
    place();
    const raf = window.requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  const single = sections.length === 1;

  return (
    <span className={`perception-signals-help ${wrapClassName}`.trim()} ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className={`metric-help-btn perception-signals-help-btn ${btnClassName}${open ? " open" : ""}`.trim()}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
      >
        <HelpCircle size={iconSize} strokeWidth={2.25} aria-hidden />
      </button>
      {open ? (
        <div
          ref={popRef}
          className={`perception-signals-help-pop cost-popover cost-popover--fixed${single ? " perception-signals-help-pop--single" : ""}`}
          role="dialog"
          aria-label={dialogTitle}
          style={pos ? { top: pos.top, left: pos.left, maxHeight: pos.maxHeight } : { visibility: "hidden" }}
        >
          <header>
            <b>{dialogTitle}</b>
          </header>
          {single ? (
            <p className="perception-signals-help-single">{sections[0].body}</p>
          ) : (
            <dl className="perception-signals-help-list">
              {sections.map((section) => (
                <div key={section.title} className="perception-signals-help-item">
                  <dt>{section.title}</dt>
                  <dd>{section.body}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      ) : null}
    </span>
  );
}

function PerceptionSignalsHelp() {
  return (
    <SessionMetricHelpPopover
      wrapClassName="perception-signals-help--hero"
      btnClassName="perception-signals-help-btn"
      ariaLabel="About the score, mood label, and mood chips"
      dialogTitle="Score & mood"
      sections={[
        { title: "Overall score", body: SESSION_SIGNAL_HELP.moodScore },
        { title: "Mood here", body: SESSION_SIGNAL_HELP.moodLabel },
        { title: "Mood shifts", body: SESSION_SIGNAL_HELP.moodLabelsTotal },
      ]}
    />
  );
}

export function SessionDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const profile = useAuthProfile();
  const { impersonation } = useRoleImpersonation();
  const canViewFinance = hasPermission(profile?.permissions, "finance.view");
  const canExportReport = hasPermission(profile?.permissions, "sessions.export_report");
  const [detail, setDetail] = useState<SessionDetail | null>(null);
  const [topicMoments, setTopicMoments] = useState<
    Array<{ at: number; label: string; color?: string }>
  >([]);
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [costOpen, setCostOpen] = useState(false);
  const [costPos, setCostPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "live" | "error">("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [tab, setTab] = useState("Signals");
  const [txQuery, setTxQuery] = useState("");
  const [flagBusy, setFlagBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [removeVideoOpen, setRemoveVideoOpen] = useState(false);
  const [removeVideoBusy, setRemoveVideoBusy] = useState(false);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunPhase, setRerunPhase] = useState<"confirm" | "queued" | "blocked" | "error">("confirm");
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [rate, setRate] = useState(1);
  const [muted, setMuted] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFrameReady, setVideoFrameReady] = useState(false);
  const [modalFrameReady, setModalFrameReady] = useState(false);
  const [modalBotFrameReady, setModalBotFrameReady] = useState(false);
  const [userPortrait, setUserPortrait] = useState<string | null>(null);
  const [videoMissing, setVideoMissing] = useState(false);
  const [videoDurationSec, setVideoDurationSec] = useState(0);
  const [botVideoReady, setBotVideoReady] = useState(false);
  const [botVideoMissing, setBotVideoMissing] = useState(false);
  const [botVideoDurationSec, setBotVideoDurationSec] = useState(0);
  const [archiveAudioDurationSec, setArchiveAudioDurationSec] = useState(0);
  const posterDoneRef = useRef(false);
  const costRef = useRef<HTMLSpanElement | null>(null);
  const costPopoverRef = useRef<HTMLDivElement | null>(null);
  const costBtnRef = useRef<HTMLButtonElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const buffersRef = useRef<Map<number, AudioBuffer>>(new Map());
  const bufferPlayRef = useRef<{ index: number; originSec: number; ctxStart: number; rate: number; token: number } | null>(null);
  const bufferTokenRef = useRef(0);
  const bufferSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const slotRefs = useRef<[HTMLAudioElement | null, HTMLAudioElement | null]>([null, null]);
  const secondaryAudioRef = useRef<HTMLAudioElement | null>(null);
  const activeSlotRef = useRef(0);
  /** One <video> per slot — moved plate↔modal via appendChild (no second S3 Range GET). */
  const plateVideoRef = useRef<HTMLVideoElement | null>(null);
  const botPlateVideoRef = useRef<HTMLVideoElement | null>(null);
  const userPlateHostRef = useRef<HTMLDivElement | null>(null);
  const botPlateHostRef = useRef<HTMLDivElement | null>(null);
  const userModalHostRef = useRef<HTMLDivElement | null>(null);
  const botModalHostRef = useRef<HTMLDivElement | null>(null);
  const segIndexRef = useRef(0);
  const playlistRef = useRef<Seg[]>([]);
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const transcriptListRef = useRef<HTMLDivElement | null>(null);
  const seekingRef = useRef(false);
  const gapTimerRef = useRef<number | undefined>(undefined);
  const playingRef = useRef(false);
  const rateRef = useRef(1);
  const clockOffsetSecRef = useRef(0);
  const playSegmentAtRef = useRef<(index: number, offsetSec?: number) => void>(() => {});
  const afterSegmentRef = useRef<(index: number) => void>(() => {});
  const currentRef = useRef(0);
  const durationRef = useRef(1);
  const playheadRef = useRef<HTMLSpanElement | null>(null);
  const timecodeRef = useRef<HTMLSpanElement | null>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const clockPaintAtRef = useRef(0);
  const videoSeekLockRef = useRef(new WeakMap<HTMLMediaElement, boolean>());
  // Prefetch archive audio: decode primary into AudioBuffer (Web Audio clock — no
  // HTMLMediaElement underruns) and blob-cache secondary for HTML sync.
  const archiveBlobRef = useRef<{
    primarySigned: string;
    primaryBlob: string;
    secondarySigned: string;
    secondaryBlob: string;
    secondaryPromise: Promise<string> | null;
  }>({
    primarySigned: "",
    primaryBlob: "",
    secondarySigned: "",
    secondaryBlob: "",
    secondaryPromise: null,
  });
  const timelineBufferRef = useRef<AudioBuffer | null>(null);
  const timelineDecodePromiseRef = useRef<Promise<AudioBuffer | null> | null>(null);
  const [archiveBlobTick, setArchiveBlobTick] = useState(0);

  useEffect(() => { playingRef.current = playing; }, [playing]);
  rateRef.current = rate;

  useEffect(() => {
    if (!actionsOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(".row-action-wrap")) return;
      setActionsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [actionsOpen]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);
    setDetail(null);
    setPlaying(false);
    currentRef.current = 0;
    setCurrent(0);
    void (async () => {
      try {
        const live = await api.getSession(id);
        if (cancelled) return;
        if (
          impersonation
          && isHiddenByRoleScope(live, profile, { ignoreOwnerException: true })
        ) {
          setDetail(null);
          setLoadState("error");
          setLoadError("Session not found");
          return;
        }
        setDetail(live);
        setLoadState("live");
      } catch (e) {
        if (cancelled) return;
        setDetail(null);
        setLoadState("error");
        setLoadError(e instanceof Error ? e.message : "Session not found");
      }
    })();
    return () => { cancelled = true; };
  }, [id, impersonation?.roleId, profile?.roleId, profile?.allowedAgentIds?.join("|")]);

  useEffect(() => {
    if (!id || !perceptionPassInFlight(detail?.videoPerceptionStatus)) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api.getSession(id);
        if (!cancelled) {
          setDetail((prev) => stabilizeSessionDetailMedia(prev, next));
        }
      } catch {
        /* keep last known detail */
      }
    };
    const t = window.setInterval(() => { void tick(); }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [id, detail?.videoPerceptionStatus]);

  const paintClock = useCallback((sec: number, commit = false) => {
    const capped = Math.max(0, Math.min(durationRef.current || 1, sec));
    currentRef.current = capped;
    const dur = durationRef.current || 1;
    if (playheadRef.current) playheadRef.current.style.left = `${(capped / dur) * 100}%`;
    if (timecodeRef.current) timecodeRef.current.textContent = time(capped);
    // Wavebar fills must track the clock every frame — React `current` is throttled
    // (~250ms) and would light bars in clumps if used for --fill.
    const root = waveformRef.current;
    if (root) {
      const laneList = [...root.querySelectorAll<HTMLElement>(".wave-lane-bars")];
      if (!laneList.length) laneList.push(root);
      for (const lane of laneList) {
        const bars = lane.getElementsByClassName("wavebar");
        const n = bars.length;
        if (n <= 0) continue;
        const x = (capped / dur) * n;
        for (let i = 0; i < n; i += 1) {
          const fill = Math.max(0, Math.min(1, x - i));
          const el = bars[i] as HTMLElement;
          el.style.setProperty("--fill", `${fill * 100}%`);
        }
      }
    }
    const now = performance.now();
    if (!commit && now - clockPaintAtRef.current < 250) return;
    clockPaintAtRef.current = now;
    setCurrent(capped);
  }, []);
  const hasBotPeaks = (detail?.waveformPeaksBot?.length ?? 0) > 0;
  const userBarMax = hasBotPeaks ? WAVEFORM_BAR_MAX_DUO_LANE : WAVEFORM_BAR_MAX_SINGLE;
  const waves = useMemo(
    () =>
      prepareWaveform(
        detail?.waveformPeaks,
        300,
        userBarMax,
        hasBotPeaks ? { floor: 3 } : undefined,
      ),
    [detail?.waveformPeaks, userBarMax, hasBotPeaks],
  );
  const botWaves = useMemo(() => {
    const raw = detail?.waveformPeaksBot;
    if (!raw?.length) return [] as number[];
    const clockDur = Math.max(1, Number(detail?.durationSec) || 1);
    const botOff = Math.max(0, Number(detail?.trackOffsetsSec?.botAudio) || 0);
    const shifted = shiftPeaksForClock(raw, botOff, clockDur) || raw;
    return prepareWaveform(shifted, 300, WAVEFORM_BAR_MAX_DUO_LANE, { floor: 3 });
  }, [detail?.waveformPeaksBot, detail?.durationSec, detail?.trackOffsetsSec?.botAudio]);
  const duoWave = botWaves.length > 0;
  const peaksPending = archivePeaksPending(detail, waves.length);

  useEffect(() => {
    if (!peaksPending) return;
    setPlaying(false);
  }, [peaksPending]);

  useEffect(() => {
    if (!id || !peaksPending) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const next = await api.getSession(id);
        if (!cancelled) {
          setDetail((prev) => stabilizeSessionDetailMedia(prev, next));
        }
      } catch {
        /* keep last known detail */
      }
    };
    const t = window.setInterval(() => { void tick(); }, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(t);
    };
  }, [id, peaksPending]);

  // Topic diamonds from Interaction Profile report moments (never merged into keyMoments).
  useEffect(() => {
    setTopicMoments([]);
    const stored = Array.isArray(detail?.topicMoments) ? detail!.topicMoments! : [];
    if (stored.length) {
      setTopicMoments(stored.map((m) => ({
        at: Number(m.at) || 0,
        label: String(m.label || "").slice(0, 48),
        color: m.color || "#94a3b8",
      })));
      return;
    }
    const url = (detail?.perceptionReportUrl || "").trim();
    if (!url) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok || cancelled) return;
        const json = (await res.json()) as {
          moments?: Array<{ at?: number; label?: string; tone?: string; color?: string }>;
        };
        const dur = Math.max(1, Number(detail?.durationSec) || 1);
        const mapped = (json.moments || [])
          .map((m) => {
            const label = String(m?.label || "").trim();
            if (!label) return null;
            const at = Math.max(0, Math.min(dur, Math.round(Number(m.at) || 0)));
            const tone = String(m.tone || "").toLowerCase();
            let color = "#94a3b8";
            if (tone === "warn") color = "#ef4444";
            else if (tone === "good") color = "#0aa878";
            else if (m.color) color = String(m.color);
            return { at, label: label.slice(0, 48), color };
          })
          .filter((m): m is { at: number; label: string; color: string } => Boolean(m))
          .slice(0, 12);
        if (!cancelled && mapped.length) setTopicMoments(mapped);
      } catch {
        /* keep empty */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.topicMoments, detail?.perceptionReportUrl, detail?.durationSec]);

  // Emotion circles only — from fused keyMoments (no report topic fallback).
  const moments = useMemo(() => detail?.keyMoments || [], [detail?.keyMoments]);
  const playlist = useMemo(() => buildPlaylist(detail), [detail]);
  playlistRef.current = playlist;
  const hasTimeline = Boolean(detail?.timelineAudioUrl || detail?.audioUrl);
  const hasSegments = playlist.length > 0 && !hasTimeline;
  const trackOffsets = detail?.trackOffsetsSec || {};
  const clockSource = detail?.clockAudioSource || null;
  const clockOffsetSec = Math.max(
    0,
    Number(
      clockSource === "botAudio"
        ? trackOffsets.botAudio
        : clockSource === "userAudio"
          ? trackOffsets.userAudio
          : 0,
    ) || 0,
  );
  clockOffsetSecRef.current = clockOffsetSec;
  const videoRemoved = detail?.videoRemoved === true;
  const archivePlayback = useMemo(
    () => computeArchivePlaybackState(detail, waves.length, videoRemoved),
    [detail, waves.length, videoRemoved],
  );
  const userVideoUrl = videoRemoved ? "" : (detail?.userVideoUrl || "").trim();
  const botVideoUrl = videoRemoved ? "" : (detail?.botVideoUrl || "").trim();
  const userVideoObject = archiveObjectIdentity(userVideoUrl);
  const botVideoObject = archiveObjectIdentity(botVideoUrl);
  // Signed S3 URLs + browser Range (preload=auto). Never full-fetch multi-MB WebMs.
  const playbackUserVideoUrl = userVideoUrl;
  const playbackBotVideoUrl = botVideoUrl;
  const hasUserVideo = Boolean(playbackUserVideoUrl) && videoReady && !videoMissing;
  const hasBotVideo = Boolean(playbackBotVideoUrl) && botVideoReady && !botVideoMissing;
  const canRemoveVideo = Boolean(
    !videoRemoved
    && (detail?.userVideoUrl || detail?.botVideoUrl || detail?.videoKey || detail?.videoEgressStatus === "completed"),
  );
  const showUserPlate = videoRemoved || Boolean(userVideoUrl);
  const showBotPlate = Boolean(botVideoUrl);
  const showCameraDuo = !videoRemoved && (Boolean(userVideoUrl) || Boolean(botVideoUrl));
  const canOpenVideoModal = Boolean(
    !videoRemoved
    && (
      (userVideoUrl && videoFrameReady && !videoMissing)
      || (botVideoUrl && botVideoReady && !botVideoMissing)
    ),
  );
  const secondaryArchiveUrl = useMemo(() => {
    if (!hasTimeline) return "";
    const userUrl = (detail?.userAudioUrl || "").trim();
    const botUrl = (detail?.botAudioUrl || "").trim();
    // user-rearm.ogg must count even if an older API omitted archiveUserAudio.
    const hasUser =
      Boolean(detail?.archiveUserAudio)
      || /\/audio\/user(?:-[A-Za-z0-9]+)?\.[^/?#]+/i.test(userUrl.split("?")[0] || "");
    const hasBot =
      Boolean(detail?.archiveBotAudio)
      || /\/audio\/bot(?:-[A-Za-z0-9]+)?\.[^/?#]+/i.test(botUrl.split("?")[0] || "");
    if (clockSource === "userAudio" && hasBot) return botUrl;
    if (clockSource === "botAudio" && hasUser) return userUrl;
    return "";
  }, [hasTimeline, clockSource, detail]);
  const secondaryOffsetSec = Math.max(
    0,
    Number(
      clockSource === "userAudio" ? trackOffsets.botAudio : trackOffsets.userAudio,
    ) || 0,
  );

  const primarySignedUrl = useMemo(
    () => (detail?.timelineAudioUrl || detail?.audioUrl || "").trim(),
    [detail?.timelineAudioUrl, detail?.audioUrl],
  );

  const resolveSecondaryUrl = useCallback(() => {
    const signed = secondaryArchiveUrl;
    const cache = archiveBlobRef.current;
    if (
      signed
      && sameArchiveObject(cache.secondarySigned, signed)
      && cache.secondaryBlob
    ) {
      return cache.secondaryBlob;
    }
    return signed;
  }, [secondaryArchiveUrl, archiveBlobTick]);

  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) {
      const ctx = new AudioContext();
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainRef.current = gain;
      // Chrome suspends AudioContext when the tab is backgrounded → silent /
      // looping secondary sync against a frozen playhead. Keep it running while
      // the user explicitly pressed Play.
      ctx.onstatechange = () => {
        if (playingRef.current && ctx.state === "suspended") {
          void ctx.resume().catch(() => { /* autoplay / policy */ });
        }
      };
    }
    return audioCtxRef.current;
  }, []);

  /** Authoritative session clock from Web Audio or HTML media (works when rAF is paused). */
  const readPlaybackClockSec = useCallback((): number | null => {
    const bp = bufferPlayRef.current;
    const ctx = audioCtxRef.current;
    if (bp && ctx) {
      return bp.originSec + Math.max(0, (ctx.currentTime - bp.ctxStart) * bp.rate);
    }
    const el = audioRef.current;
    if (el && !el.paused && (el.currentSrc || el.src)) {
      if (hasTimeline) return el.currentTime + clockOffsetSecRef.current;
      const seg = playlistRef.current[segIndexRef.current];
      if (seg) return (seg.startMs + el.currentTime * 1000) / 1000;
    }
    return null;
  }, [hasTimeline]);

  const ensureTimelineBuffer = useCallback(async (signed: string): Promise<AudioBuffer | null> => {
    if (!signed) return null;
    const cache = archiveBlobRef.current;
    const samePrimary = sameArchiveObject(cache.primarySigned, signed);
    if (samePrimary && timelineBufferRef.current) {
      return timelineBufferRef.current;
    }
    if (samePrimary && timelineDecodePromiseRef.current) {
      return timelineDecodePromiseRef.current;
    }
    if (cache.primaryBlob) {
      URL.revokeObjectURL(cache.primaryBlob);
      cache.primaryBlob = "";
    }
    timelineBufferRef.current = null;
    cache.primarySigned = signed;
    const promise = (async () => {
      try {
        const res = await fetch(signed);
        if (!res.ok) throw new Error(`audio fetch ${res.status}`);
        const blob = await res.blob();
        if (!sameArchiveObject(archiveBlobRef.current.primarySigned, signed)) return null;
        const blobUrl = URL.createObjectURL(blob);
        archiveBlobRef.current.primaryBlob = blobUrl;
        const raw = await blob.arrayBuffer();
        const ctx = ensureAudioCtx();
        const buf = await ctx.decodeAudioData(raw.slice(0));
        if (!sameArchiveObject(archiveBlobRef.current.primarySigned, signed)) return null;
        timelineBufferRef.current = buf;
        timelineDecodePromiseRef.current = null;
        setArchiveAudioDurationSec((prev) => Math.max(prev, buf.duration));
        setArchiveBlobTick((n) => n + 1);
        return buf;
      } catch {
        timelineDecodePromiseRef.current = null;
        return null;
      }
    })();
    timelineDecodePromiseRef.current = promise;
    return promise;
  }, [ensureAudioCtx]);

  const ensureSecondaryBlob = useCallback(async (signed: string): Promise<string> => {
    if (!signed) return "";
    const cache = archiveBlobRef.current;
    const sameSecondary = sameArchiveObject(cache.secondarySigned, signed);
    if (sameSecondary && cache.secondaryBlob) return cache.secondaryBlob;
    if (sameSecondary && cache.secondaryPromise) return cache.secondaryPromise;
    if (cache.secondaryBlob) {
      URL.revokeObjectURL(cache.secondaryBlob);
      cache.secondaryBlob = "";
    }
    cache.secondarySigned = signed;
    const promise = (async () => {
      try {
        const res = await fetch(signed);
        if (!res.ok) throw new Error(`audio fetch ${res.status}`);
        const blob = await res.blob();
        if (!sameArchiveObject(archiveBlobRef.current.secondarySigned, signed)) {
          return signed;
        }
        const blobUrl = URL.createObjectURL(blob);
        archiveBlobRef.current.secondaryBlob = blobUrl;
        archiveBlobRef.current.secondaryPromise = null;
        setArchiveBlobTick((n) => n + 1);
        return blobUrl;
      } catch {
        archiveBlobRef.current.secondaryPromise = null;
        return signed;
      }
    })();
    cache.secondaryPromise = promise;
    return promise;
  }, []);

  // Prefetch + decode as soon as signed URLs are available.
  useEffect(() => {
    if (!primarySignedUrl && !secondaryArchiveUrl) return;
    let cancelled = false;
    void (async () => {
      const jobs: Promise<unknown>[] = [];
      if (primarySignedUrl) jobs.push(ensureTimelineBuffer(primarySignedUrl));
      if (secondaryArchiveUrl) jobs.push(ensureSecondaryBlob(secondaryArchiveUrl));
      await Promise.all(jobs);
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [primarySignedUrl, secondaryArchiveUrl, ensureTimelineBuffer, ensureSecondaryBlob]);

  useEffect(() => () => {
    const cache = archiveBlobRef.current;
    if (cache.primaryBlob) URL.revokeObjectURL(cache.primaryBlob);
    if (cache.secondaryBlob) URL.revokeObjectURL(cache.secondaryBlob);
    archiveBlobRef.current = {
      primarySigned: "",
      primaryBlob: "",
      secondarySigned: "",
      secondaryBlob: "",
      secondaryPromise: null,
    };
    timelineBufferRef.current = null;
    timelineDecodePromiseRef.current = null;
  }, []);

  const primaryAudioObject = archiveObjectIdentity(
    detail?.timelineAudioUrl || detail?.audioUrl || "",
  );

  // Reset plates only when the underlying archive object changes — not when a
  // poll re-signs the same S3 key (that used to clear the avatar + flash video).
  useEffect(() => {
    posterDoneRef.current = false;
    setVideoReady(false);
    setVideoFrameReady(false);
    setModalFrameReady(false);
    setModalBotFrameReady(false);
    setUserPortrait(null);
    setVideoMissing(false);
    setVideoDurationSec(0);
    setVideoOpen(false);
  }, [userVideoObject, userVideoUrl]);
  useEffect(() => {
    setBotVideoReady(false);
    setBotVideoMissing(false);
    setBotVideoDurationSec(0);
    setModalBotFrameReady(false);
  }, [botVideoObject, botVideoUrl]);
  useEffect(() => {
    setArchiveAudioDurationSec(0);
  }, [primaryAudioObject]);
  const videoOffsetSec = Math.max(0, Number(trackOffsets.userVideo ?? detail?.videoOffsetSec) || 0);
  const botVideoOffsetSec = Math.max(0, Number(trackOffsets.botVideo) || 0);
  const playlistEndSec = Math.max(
    0,
    ...playlist.map((s) => (Number(s.endMs) || Number(s.startMs) || 0) / 1000),
  );
  const sessionDurSec = Number(detail?.durationSec) || 0;
  // Client MediaRecorder WebM often has no Duration cue (browser reports Infinity /
  // NaN). Fall back to archive audio / session length so plates are not stuck in
  // permanent is-gap blur (videoLive requires a finite span).
  const mediaDurationHintSec = Math.max(
    archiveAudioDurationSec > 0 ? archiveAudioDurationSec : 0,
    sessionDurSec,
  );
  const userVideoSpanSec = videoDurationSec > 0 ? videoDurationSec : mediaDurationHintSec;
  const botVideoSpanSec = botVideoDurationSec > 0 ? botVideoDurationSec : mediaDurationHintSec;
  const videoEndSec = userVideoSpanSec > 0 ? videoOffsetSec + userVideoSpanSec : 0;
  const botVideoEndSec = botVideoSpanSec > 0 ? botVideoOffsetSec + botVideoSpanSec : 0;
  const archiveEndSec = archiveAudioDurationSec > 0 ? clockOffsetSec + archiveAudioDurationSec : 0;
  const mediaEndSec = Math.max(playlistEndSec, videoEndSec, botVideoEndSec, archiveEndSec);
  // When continuous archive is known, scrubber length follows audible media.
  // Wall-clock durationSec often includes hangup drain and leaves the playhead
  // stuck ~2/3 along the waveform after goodbye audio ends.
  const duration = Math.max(
    1,
    archiveEndSec > 0 ? mediaEndSec : Math.max(sessionDurSec, mediaEndSec),
  );
  durationRef.current = duration;
  const displayMoments = useMemo(() => {
    const out: typeof moments = [];
    for (const m of moments) {
      const prev = out[out.length - 1];
      if (prev && String(prev.label).toLowerCase() === String(m.label).toLowerCase()) continue;
      out.push(m);
    }
    return out;
  }, [moments]);
  // Re-apply bar fills after React remounts wavebars (height-only style).
  useLayoutEffect(() => {
    paintClock(currentRef.current, true);
  }, [waves, botWaves, duration, paintClock]);
  const videoLive = Boolean(
    hasUserVideo
    && userVideoSpanSec > 0
    && current + 0.04 >= Math.max(0, videoOffsetSec - 0.05)
    && current < videoOffsetSec + userVideoSpanSec - 0.04,
  );
  const botVideoLive = Boolean(
    hasBotVideo
    && botVideoSpanSec > 0
    && current + 0.04 >= Math.max(0, botVideoOffsetSec - 0.05)
    && current < botVideoOffsetSec + botVideoSpanSec - 0.04,
  );

  const revealVideoFrame = useCallback(() => {
    if (posterDoneRef.current) return;
    posterDoneRef.current = true;
    setVideoFrameReady(true);
    setVideoReady(true);
  }, []);

  const armVideoPoster = useCallback((el: HTMLVideoElement | null, audioSec: number) => {
    if (!el || posterDoneRef.current) return;
    const target = Math.max(0, audioSec - videoOffsetSec);
    const capped = Number.isFinite(el.duration) && el.duration > 0
      ? Math.min(target, Math.max(0, el.duration - 0.05))
      : target;
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => revealVideoFrame()));
    };
    const timer = window.setTimeout(finish, 4000);
    const done = () => {
      window.clearTimeout(timer);
      finish();
    };
    if (el.readyState >= 2 && Math.abs(el.currentTime - capped) < 0.08) {
      done();
      return;
    }
    el.addEventListener("seeked", done, { once: true });
    try {
      if (Math.abs(el.currentTime - capped) >= 0.08) el.currentTime = capped;
      else el.addEventListener("loadeddata", done, { once: true });
    } catch {
      done();
    }
  }, [revealVideoFrame, videoOffsetSec]);

  const VIDEO_LEAD_SEC = 0.05; // start video slightly ahead — HTMLVideoElement trails Web Audio by a few dozen ms
  /** Soft A/V catch-up while playing — hard seeks thrash WebM decode and look like lag. */
  const PLAY_RATE_NUDGE_SEC = 0.08;
  const PLAY_HARD_SEEK_SEC = 2.0;
  const SNAP_SEC = 0.05;
  const MAX_CATCHUP_MULT = 1.2;
  const MIN_CATCHUP_MULT = 0.85;

  const videoTarget = useCallback((el: HTMLVideoElement, audioSec: number) => {
    const target = Math.max(0, audioSec - videoOffsetSec + VIDEO_LEAD_SEC);
    return Number.isFinite(el.duration) && el.duration > 0
      ? Math.min(target, Math.max(0, el.duration - 0.05))
      : target;
  }, [videoOffsetSec]);

  const hardSeekMedia = useCallback((el: HTMLMediaElement, t: number) => {
    if (videoSeekLockRef.current.get(el)) return;
    videoSeekLockRef.current.set(el, true);
    const unlock = () => {
      videoSeekLockRef.current.delete(el);
      el.removeEventListener("seeked", unlock);
    };
    el.addEventListener("seeked", unlock);
    window.setTimeout(unlock, 3000);
    try {
      el.currentTime = t;
    } catch {
      unlock();
    }
  }, []);

  /**
   * Keep muted archive video/audio near the Web Audio session clock.
   * While playing: nudge playbackRate for small drift; hard-seek only when
   * extreme (or when (re)starting / pausing so the parked frame is correct).
   */
  const syncMediaToClock = useCallback((
    el: HTMLMediaElement,
    capped: number,
    shouldPlay: boolean,
    transportRate: number,
  ) => {
    // Progressive S3 WebM: never seek past what Range has already buffered.
    const seekTarget = clampMediaTimeToBuffered(el, capped);
    const drift = el.currentTime - seekTarget;
    const absDrift = Math.abs(drift);
    const starved = seekTarget + 0.12 < capped;

    if (!shouldPlay) {
      if (Math.abs(el.playbackRate - transportRate) > 0.01) applyMediaPlaybackRate(el, transportRate);
      if (absDrift > SNAP_SEC) hardSeekMedia(el, seekTarget);
      if (!el.paused) el.pause();
      return;
    }

    if (el.paused) {
      // One snap when entering play — then free-run with rate nudges.
      if (absDrift > SNAP_SEC) {
        try {
          el.currentTime = seekTarget;
        } catch { /* seek race */ }
      }
      applyMediaPlaybackRate(el, transportRate);
      void el.play().catch(() => { /* autoplay policy / missing src */ });
      return;
    }

    // Clock ran ahead of the progressive buffer — hold the last buffered frame.
    if (starved) {
      if (!el.paused) el.pause();
      if (absDrift > SNAP_SEC) hardSeekMedia(el, seekTarget);
      return;
    }

    if (absDrift > PLAY_HARD_SEEK_SEC) {
      hardSeekMedia(el, seekTarget);
      applyMediaPlaybackRate(el, transportRate);
      return;
    }

    if (absDrift <= PLAY_RATE_NUDGE_SEC) {
      if (Math.abs(el.playbackRate - transportRate) > 0.01) applyMediaPlaybackRate(el, transportRate);
      return;
    }

    // Behind clock (currentTime < target) → speed up; ahead → slow down.
    if (drift < 0) {
      const boost = Math.min(MAX_CATCHUP_MULT, 1 + Math.min(0.2, -drift * 0.35));
      applyMediaPlaybackRate(el, transportRate * boost);
    } else {
      const slow = Math.max(MIN_CATCHUP_MULT, 1 - Math.min(0.15, drift * 0.25));
      applyMediaPlaybackRate(el, transportRate * slow);
    }
  }, [hardSeekMedia]);

  const syncVideoToAudio = useCallback((el: HTMLVideoElement | null, audioSec: number, shouldPlay: boolean) => {
    if (!el) return;
    el.muted = true;
    el.playsInline = true;
    const hint = Math.max(videoDurationSec, archiveAudioDurationSec, Number(detail?.durationSec) || 0);
    const vidDur = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : hint;
    const liveFrom = videoOffsetSec - VIDEO_LEAD_SEC;
    // Outside the live span: pause and park on first/last frame so the plate can
    // show a blurred cover instead of a misleading "No video" empty state.
    if (audioSec + 0.05 < liveFrom) {
      if (!el.paused) el.pause();
      if (Math.abs(el.playbackRate - rate) > 0.01) applyMediaPlaybackRate(el, rate);
      if (Math.abs(el.currentTime) > 0.08) hardSeekMedia(el, 0);
      return;
    }
    if (vidDur > 0 && audioSec >= videoOffsetSec + vidDur - 0.02) {
      if (!el.paused) el.pause();
      if (Math.abs(el.playbackRate - rate) > 0.01) applyMediaPlaybackRate(el, rate);
      const last = Math.max(0, vidDur - 0.05);
      if (Math.abs(el.currentTime - last) > 0.08) hardSeekMedia(el, last);
      return;
    }
    syncMediaToClock(el, videoTarget(el, audioSec), shouldPlay, rate);
  }, [rate, videoDurationSec, archiveAudioDurationSec, detail?.durationSec, videoOffsetSec, videoTarget, syncMediaToClock, hardSeekMedia]);

  const syncTrackMedia = useCallback((
    el: HTMLMediaElement | null,
    audioSec: number,
    offsetSec: number,
    shouldPlay: boolean,
    opts?: { muted?: boolean; durationHint?: number },
  ) => {
    if (!el) return;
    if (opts?.muted != null) el.muted = opts.muted;
    const vidDur = Number.isFinite(el.duration) && el.duration > 0
      ? el.duration
      : (opts?.durationHint || 0);
    const liveFrom = offsetSec - 0.05;
    if (audioSec + 0.05 < liveFrom) {
      if (!el.paused) el.pause();
      if (Math.abs(el.playbackRate - rate) > 0.01) applyMediaPlaybackRate(el, rate);
      if (el instanceof HTMLVideoElement && Math.abs(el.currentTime) > 0.08) {
        hardSeekMedia(el, 0);
      }
      return;
    }
    if (vidDur > 0 && audioSec >= offsetSec + vidDur - 0.02) {
      if (!el.paused) el.pause();
      if (Math.abs(el.playbackRate - rate) > 0.01) applyMediaPlaybackRate(el, rate);
      if (el instanceof HTMLVideoElement) {
        const last = Math.max(0, vidDur - 0.05);
        if (Math.abs(el.currentTime - last) > 0.08) hardSeekMedia(el, last);
      }
      return;
    }
    const target = Math.max(0, audioSec - offsetSec + 0.05);
    const capped = vidDur > 0 ? Math.min(target, Math.max(0, vidDur - 0.05)) : target;
    syncMediaToClock(el, capped, shouldPlay, rate);
  }, [rate, syncMediaToClock, hardSeekMedia]);

  useEffect(() => {
    if (!videoFrameReady) return;
    const el = plateVideoRef.current;
    if (!el) return;
    const portrait = capturePortrait(el);
    if (portrait) setUserPortrait(portrait);
  }, [videoFrameReady]);

  // Imperative <video> nodes: one per slot, appendChild between plate/modal hosts
  // so opening Cameras never starts a second S3 Range download.
  useLayoutEffect(() => {
    const mountSlot = (opts: {
      url: string;
      objectId: string;
      videoRef: MutableRefObject<HTMLVideoElement | null>;
      plateHost: HTMLDivElement | null;
      modalHost: HTMLDivElement | null;
      onMeta: (el: HTMLVideoElement) => void;
      onError: () => void;
    }) => {
      if (!opts.url || !opts.objectId) return () => {};
      let el = opts.videoRef.current;
      if (!el) {
        el = document.createElement("video");
        el.muted = true;
        el.playsInline = true;
        el.preload = "auto";
        el.setAttribute("playsinline", "");
        opts.videoRef.current = el;
      }
      const onMeta = () => opts.onMeta(el!);
      const onErr = () => opts.onError();
      el.addEventListener("loadedmetadata", onMeta);
      el.addEventListener("loadeddata", onMeta);
      el.addEventListener("error", onErr);
      if (el.dataset.objectId !== opts.objectId) {
        el.dataset.objectId = opts.objectId;
        el.src = opts.url;
      }
      const host = videoOpen ? opts.modalHost : opts.plateHost;
      if (host && el.parentElement !== host) host.appendChild(el);
      return () => {
        el?.removeEventListener("loadedmetadata", onMeta);
        el?.removeEventListener("loadeddata", onMeta);
        el?.removeEventListener("error", onErr);
      };
    };

    const stopUser = mountSlot({
      url: playbackUserVideoUrl,
      objectId: userVideoObject,
      videoRef: plateVideoRef,
      plateHost: userPlateHostRef.current,
      modalHost: userModalHostRef.current,
      onMeta: (el) => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          setVideoDurationSec(el.duration);
        } else {
          const hint = Math.max(
            archiveAudioDurationSec,
            Number(detail?.durationSec) || 0,
          );
          if (hint > 0) setVideoDurationSec(hint);
        }
        armVideoPoster(el, currentRef.current);
        if (videoOpen) setModalFrameReady(true);
      },
      onError: () => {
        setVideoReady(false);
        setVideoFrameReady(false);
        setVideoMissing(true);
        setVideoOpen(false);
      },
    });
    const stopBot = mountSlot({
      url: playbackBotVideoUrl,
      objectId: botVideoObject,
      videoRef: botPlateVideoRef,
      plateHost: botPlateHostRef.current,
      modalHost: botModalHostRef.current,
      onMeta: (el) => {
        const hint = Math.max(
          archiveAudioDurationSec,
          Number(detail?.durationSec) || 0,
        );
        const span = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : hint;
        if (span > 0) setBotVideoDurationSec(span);
        setBotVideoReady(true);
        syncTrackMedia(el, currentRef.current, botVideoOffsetSec, false, {
          muted: true,
          durationHint: span,
        });
        if (videoOpen) setModalBotFrameReady(true);
      },
      onError: () => {
        setBotVideoReady(false);
        setBotVideoMissing(true);
      },
    });
    let raf = 0;
    if (videoOpen) {
      if (plateVideoRef.current && plateVideoRef.current.readyState >= 2) setModalFrameReady(true);
      if (botPlateVideoRef.current && botPlateVideoRef.current.readyState >= 2) setModalBotFrameReady(true);
      // Dialog content may commit one frame after open.
      raf = window.requestAnimationFrame(() => {
        const userEl = plateVideoRef.current;
        const botEl = botPlateVideoRef.current;
        if (userEl && userModalHostRef.current && userEl.parentElement !== userModalHostRef.current) {
          userModalHostRef.current.appendChild(userEl);
        }
        if (botEl && botModalHostRef.current && botEl.parentElement !== botModalHostRef.current) {
          botModalHostRef.current.appendChild(botEl);
        }
      });
    }
    return () => {
      stopUser();
      stopBot();
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [
    playbackUserVideoUrl,
    playbackBotVideoUrl,
    userVideoObject,
    botVideoObject,
    videoOpen,
    archiveAudioDurationSec,
    detail?.durationSec,
    botVideoOffsetSec,
    armVideoPoster,
    syncTrackMedia,
  ]);

  useEffect(() => () => {
    for (const el of [plateVideoRef.current, botPlateVideoRef.current]) {
      if (!el) continue;
      try { el.pause(); } catch { /* ignore */ }
      el.removeAttribute("src");
      el.load();
      el.remove();
    }
    plateVideoRef.current = null;
    botPlateVideoRef.current = null;
  }, []);

  useEffect(() => {
    if (!hasUserVideo) return;
    const el = plateVideoRef.current;
    if (!el) return;
    syncVideoToAudio(el, currentRef.current, playing);
  }, [playing, userVideoUrl, videoOpen, hasUserVideo, syncVideoToAudio]);

  useEffect(() => {
    if (!hasBotVideo) return;
    const el = botPlateVideoRef.current;
    if (!el) return;
    syncTrackMedia(
      el,
      currentRef.current,
      botVideoOffsetSec,
      playing && botVideoLive,
      { muted: true, durationHint: botVideoDurationSec },
    );
  }, [playing, hasBotVideo, videoOpen, botVideoOffsetSec, botVideoLive, botVideoDurationSec, syncTrackMedia]);

  useEffect(() => {
    if (!playing || !hasUserVideo) return;
    const id = window.setInterval(() => {
      syncVideoToAudio(plateVideoRef.current, currentRef.current, true);
    }, 250);
    return () => window.clearInterval(id);
  }, [playing, hasUserVideo, videoOpen, syncVideoToAudio]);

  useEffect(() => {
    if (!playing || !hasBotVideo) return;
    const id = window.setInterval(() => {
      syncTrackMedia(
        botPlateVideoRef.current,
        currentRef.current,
        botVideoOffsetSec,
        true,
        { muted: true, durationHint: botVideoDurationSec },
      );
    }, 250);
    return () => window.clearInterval(id);
  }, [playing, hasBotVideo, videoOpen, botVideoOffsetSec, botVideoDurationSec, syncTrackMedia]);

  useEffect(() => {
    const el = secondaryAudioRef.current;
    const url = resolveSecondaryUrl();
    if (!el || !url) return;
    if (!mediaPointsAt(el, url)) {
      el.src = url;
      el.load();
    }
    const muteSecondary = muted;
    syncTrackMedia(el, currentRef.current, secondaryOffsetSec, playing, {
      muted: muteSecondary,
    });
  }, [
    playing,
    muted,
    secondaryArchiveUrl,
    secondaryOffsetSec,
    clockSource,
    syncTrackMedia,
    resolveSecondaryUrl,
    archiveBlobTick,
  ]);

  useEffect(() => {
    if (!playing || !secondaryArchiveUrl) return;
    const id = window.setInterval(() => {
      const el = secondaryAudioRef.current;
      if (!el) return;
      syncTrackMedia(el, currentRef.current, secondaryOffsetSec, true, { muted });
    }, 250);
    return () => window.clearInterval(id);
  }, [
    playing,
    secondaryArchiveUrl,
    secondaryOffsetSec,
    muted,
    clockSource,
    syncTrackMedia,
  ]);

  // Decode every turn wav in parallel as soon as the playlist exists.
  // Playback uses these AudioBuffers, so the next turn does not wait on the network.
  useEffect(() => {
    if (hasTimeline || !playlist.length) return;
    let cancelled = false;
    const ctx = ensureAudioCtx();
    buffersRef.current = new Map();
    void Promise.all(playlist.map(async (seg, index) => {
      try {
        const res = await fetch(seg.audioUrl);
        if (!res.ok || cancelled) return;
        const raw = await res.arrayBuffer();
        if (cancelled) return;
        const audioBuf = await ctx.decodeAudioData(raw.slice(0));
        if (cancelled) return;
        buffersRef.current.set(index, audioBuf);
      } catch {
        /* signed URL fallback stays on the audio element */
      }
    }));
    return () => {
      cancelled = true;
      buffersRef.current = new Map();
    };
  }, [playlist, hasTimeline, ensureAudioCtx]);

  const transcriptRows = useMemo(() => {
    const base = detail?.transcripts?.length
      ? detail.transcripts
      : (detail?.turns || []).map((t) => ({ role: t.role, text: t.text, startMs: t.startMs }));
    const rows = (base || []).map((t) => {
      const raw = (t.text || "").trim();
      const unrecognized = isSttUnrecognized(raw, (t as { unrecognized?: boolean }).unrecognized);
      const kind = String((t as { kind?: string }).kind || "");
      const debug = kind === "live_perception_debug";
      return {
        at: (Number(t.startMs) || 0) / 1000,
        speaker: debug ? "Live Perception" : t.role === "user" ? "User" : "Agent",
        agent: debug ? false : t.role !== "user",
        text: sttUnrecognizedDisplayText(raw, unrecognized),
        unrecognized,
        debug,
        frameUrl: String((t as { frameUrl?: string; url?: string }).frameUrl || (t as { url?: string }).url || ""),
      };
    });
    const have = new Set(rows.map((r) => `${r.agent ? "assistant" : "user"}:${Math.round(r.at * 1000)}`));
    for (const t of detail?.turns || []) {
      if (!t.audioUrl || String(t.text || "").trim()) continue;
      const startMs = Number(t.startMs) || 0;
      const key = `${t.role === "user" ? "user" : "assistant"}:${Math.round(startMs)}`;
      if (have.has(key)) continue;
      rows.push({
        at: startMs / 1000,
        speaker: t.role === "user" ? "User" : "Agent",
        agent: t.role !== "user",
        text: STT_UNRECOGNIZED_LABEL,
        unrecognized: true,
        debug: false,
        frameUrl: "",
      });
      have.add(key);
    }
    rows.sort((a, b) => a.at - b.at);
    return rows.filter((r) => r.text);
  }, [detail]);

  const txSearch = txQuery.trim();
  const transcript = useMemo(() => {
    if (!txSearch) return transcriptRows;
    const q = txSearch.toLowerCase();
    return transcriptRows.filter((r) => r.text.toLowerCase().includes(q));
  }, [transcriptRows, txSearch]);

  const activeIndex = useMemo(() => {
    let idx = 0;
    for (let i = 0; i < transcript.length; i += 1) {
      if (transcript[i].at <= current) idx = i;
      else break;
    }
    return idx;
  }, [transcript, current]);

  useEffect(() => {
    const list = transcriptListRef.current;
    const row = rowRefs.current[activeIndex];
    if (!list || !row) return;
    // Scroll only the transcript pane (never the page). Keep the active
    // utterance near the top of the visible area when there is room below.
    const topPad = 10;
    const delta =
      row.getBoundingClientRect().top
      - list.getBoundingClientRect().top
      + list.scrollTop
      - topPad;
    const target = Math.max(0, Math.min(delta, list.scrollHeight - list.clientHeight));
    if (Math.abs(list.scrollTop - target) < 4) return;
    list.scrollTo({ top: target, behavior: playingRef.current ? "auto" : "smooth" });
  }, [activeIndex]);

  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      applyMediaPlaybackRate(el, rate);
      el.muted = muted;
    }
    if (gainRef.current) {
      gainRef.current.gain.value = muted ? 0 : 1;
    }
    const bp = bufferPlayRef.current;
    const src = bufferSourceRef.current;
    const ctx = audioCtxRef.current;
    if (bp && src && ctx && bp.rate !== rate) {
      const now = ctx.currentTime;
      const pos = bp.originSec + (now - bp.ctxStart) * bp.rate;
      if (rate !== 1 && bp.index === -1) {
        bufferTokenRef.current += 1;
        try { src.stop(); } catch { /* already stopped */ }
        bufferPlayRef.current = null;
        bufferSourceRef.current = null;
        const node = audioRef.current;
        if (node && playingRef.current) {
          applyMediaPlaybackRate(node, rate);
          node.muted = muted;
          const url = archiveBlobRef.current.primaryBlob || primarySignedUrl;
          if (url && !mediaPointsAt(node, url)) node.src = url;
          node.currentTime = Math.max(0, pos - clockOffsetSecRef.current);
          void node.play().catch(() => undefined);
        }
        return;
      }
      bp.originSec = pos;
      bp.ctxStart = now;
      bp.rate = rate;
      src.playbackRate.value = rate;
    }
  }, [rate, muted, clockSource, primarySignedUrl]);

  const clearGap = () => {
    if (gapTimerRef.current) {
      window.clearInterval(gapTimerRef.current);
      window.cancelAnimationFrame(gapTimerRef.current);
      gapTimerRef.current = undefined;
    }
  };

  const stopAllPlayback = useCallback(() => {
    playingRef.current = false;
    setPlaying(false);
    clearGap();
    bufferTokenRef.current += 1;
    bufferPlayRef.current = null;
    try { bufferSourceRef.current?.stop(); } catch { /* already stopped */ }
    bufferSourceRef.current = null;
    slotRefs.current.forEach((node) => {
      try { node?.pause(); } catch { /* ignore */ }
    });
    try { secondaryAudioRef.current?.pause(); } catch { /* ignore */ }
    try { plateVideoRef.current?.pause(); } catch { /* ignore */ }
    try { botPlateVideoRef.current?.pause(); } catch { /* ignore */ }
    if (gainRef.current) gainRef.current.gain.value = 0;
    try { void audioCtxRef.current?.suspend(); } catch { /* ignore */ }
  }, []);

  // Hard-stop audio/video on leave — Web Audio keeps playing after unmount otherwise.
  useEffect(() => () => { stopAllPlayback(); }, [stopAllPlayback]);

  /** Advance playhead through silence gaps (leading or between clips) with no audio. */
  const softFillGap = useCallback((fromSec: number, toSec: number, onDone: () => void) => {
    const startWall = performance.now();
    const gapMs = Math.max(0, (toSec - fromSec) * 1000);
    clearGap();
    if (gapMs <= 16) {
      paintClock(toSec, true);
      onDone();
      return;
    }
    // Prefer setInterval over rAF — background tabs throttle/freeze rAF and the
    // playhead would stall while media sync keeps seeking the same chunk.
    const tick = () => {
      if (!playingRef.current) {
        clearGap();
        return;
      }
      const elapsed = (performance.now() - startWall) * rateRef.current;
      const next = fromSec + elapsed / 1000;
      if (elapsed >= gapMs) {
        clearGap();
        paintClock(toSec, true);
        onDone();
        return;
      }
      paintClock(next);
    };
    gapTimerRef.current = window.setInterval(tick, 50);
  }, [paintClock]);

  /** Start decoded timeline primary via Web Audio at a session-clock position. */
  const startTimelineBuffer = useCallback((sessionSec: number) => {
    const buf = timelineBufferRef.current;
    const ctx = ensureAudioCtx();
    const gain = gainRef.current;
    if (!buf || !gain) return false;
    // Web Audio buffer playback changes pitch at non-1x; HTML audio uses preservesPitch.
    if ((rateRef.current || 1) !== 1) return false;

    clearGap();
    bufferTokenRef.current += 1;
    const token = bufferTokenRef.current;
    try { bufferSourceRef.current?.stop(); } catch { /* already stopped */ }
    slotRefs.current.forEach((node) => node?.pause());

    void ctx.resume();
    gain.gain.value = muted ? 0 : 1;

    const offset = clockOffsetSecRef.current;
    const mediaOffset = Math.min(
      Math.max(0, sessionSec - offset),
      Math.max(0, buf.duration - 0.02),
    );
    if (sessionSec + 0.02 < offset) {
      softFillGap(sessionSec, offset, () => {
        if (!playingRef.current) return;
        startTimelineBuffer(offset);
      });
      return true;
    }
    if (mediaOffset >= buf.duration - 0.02) {
      paintClock(offset + buf.duration, true);
      setPlaying(false);
      return true;
    }

    const rateNow = rateRef.current || 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rateNow;
    src.connect(gain);
    const originSec = offset + mediaOffset;
    bufferPlayRef.current = { index: -1, originSec, ctxStart: ctx.currentTime, rate: rateNow, token };
    bufferSourceRef.current = src;
    src.onended = () => {
      if (bufferTokenRef.current !== token) return;
      bufferPlayRef.current = null;
      bufferSourceRef.current = null;
      setPlaying(false);
    };
    src.start(0, mediaOffset);
    paintClock(originSec, true);
    playingRef.current = true;
    setPlaying(true);
    return true;
  }, [clockSource, ensureAudioCtx, muted, paintClock, softFillGap]);

  const playSegmentAt = useCallback((index: number, offsetSec = 0) => {
    const list = playlistRef.current;
    clearGap();
    if (!list.length || index < 0 || index >= list.length) {
      setPlaying(false);
      return;
    }
    segIndexRef.current = index;
    const seg = list[index];
    const start = Math.max(0, offsetSec);
    const decoded = buffersRef.current.get(index);
    const ctx = audioCtxRef.current;
    const gain = gainRef.current;
    if (decoded && ctx && gain && rate === 1) {
      bufferTokenRef.current += 1;
      const token = bufferTokenRef.current;
      try { bufferSourceRef.current?.stop(); } catch { /* already stopped */ }
      slotRefs.current.forEach((node) => node?.pause());
      void ctx.resume();
      gain.gain.value = muted ? 0 : 1;
      const src = ctx.createBufferSource();
      src.buffer = decoded;
      src.playbackRate.value = rate;
      src.connect(gain);
      const offset = Math.min(start, Math.max(0, decoded.duration - 0.02));
      const originSec = seg.startMs / 1000 + offset;
      bufferPlayRef.current = { index, originSec, ctxStart: ctx.currentTime, rate, token };
      bufferSourceRef.current = src;
      src.onended = () => {
        if (bufferTokenRef.current !== token) return;
        bufferPlayRef.current = null;
        bufferSourceRef.current = null;
        afterSegmentRef.current(index);
      };
      src.start(0, offset);
      setPlaying(true);
      return;
    }
    const activeSlot = activeSlotRef.current;
    const idleSlot = (activeSlot === 0 ? 1 : 0) as 0 | 1;
    const currentEl = slotRefs.current[activeSlot];
    const idleEl = slotRefs.current[idleSlot];
    const sameSrc = (node: HTMLAudioElement | null, url: string) => {
      if (!node || !url) return false;
      try {
        return node.src === new URL(url, window.location.href).href;
      } catch {
        return node.src === url;
      }
    };
    const idleReady = Boolean(idleEl && sameSrc(idleEl, seg.audioUrl) && idleEl.readyState >= 2);
    const el = idleReady ? idleEl : currentEl;
    if (!el) {
      setPlaying(false);
      return;
    }
    if (idleReady && idleEl) {
      currentEl?.pause();
      activeSlotRef.current = idleSlot;
      audioRef.current = idleEl;
    } else {
      audioRef.current = el;
    }
    const apply = () => {
      applyMediaPlaybackRate(el, rate);
      el.muted = muted;
      if (Math.abs(el.currentTime - start) > 0.05) el.currentTime = start;
      void el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      const nxt = list[index + 1];
      const standby = slotRefs.current[activeSlotRef.current === 0 ? 1 : 0];
      if (standby && nxt && !sameSrc(standby, nxt.audioUrl)) {
        standby.preload = "auto";
        standby.src = nxt.audioUrl;
        standby.load();
      }
    };
    if (!idleReady && !sameSrc(el, seg.audioUrl)) {
      el.src = seg.audioUrl;
      const onMeta = () => {
        apply();
        el.removeEventListener("loadedmetadata", onMeta);
      };
      el.addEventListener("loadedmetadata", onMeta);
      el.load();
    } else {
      apply();
    }
  }, [muted, rate]);

  playSegmentAtRef.current = playSegmentAt;
  afterSegmentRef.current = (index: number) => {
    const list = playlistRef.current;
    const next = index + 1;
    if (next >= list.length) {
      const cur = list[index];
      const from = (cur?.endMs || cur?.startMs || 0) / 1000;
      const endSec = durationRef.current;
      if (from < endSec - 0.05) {
        softFillGap(from, endSec, () => {
          paintClock(endSec, true);
          setPlaying(false);
        });
        return;
      }
      paintClock(endSec, true);
      setPlaying(false);
      return;
    }
    const cur = list[index];
    const nxt = list[next];
    const gapMs = Math.max(0, (nxt?.startMs || 0) - (cur?.endMs || cur?.startMs || 0));
    if (gapMs > 80) {
      softFillGap((cur.endMs || cur.startMs) / 1000, nxt.startMs / 1000, () => playSegmentAtRef.current(next, 0));
      return;
    }
    playSegmentAtRef.current(next, 0);
  };

  const seekTo = useCallback((sec: number, autoplay?: boolean) => {
    const next = Math.max(0, Math.min(duration, Number(sec) || 0));
    paintClock(next, true);
    const el = audioRef.current;
    if (!el) return;
    clearGap();
    const wantPlay = Boolean(autoplay ?? playingRef.current);
    if (wantPlay) {
      playingRef.current = true;
      setPlaying(true);
    }

    if (hasTimeline && primarySignedUrl) {
      // Prefer decoded Web Audio clock — HTMLMediaElement progressive OGG stalls.
      if (timelineBufferRef.current) {
        slotRefs.current.forEach((node) => node?.pause());
        if (wantPlay) {
          if (!startTimelineBuffer(next)) {
            playingRef.current = false;
            setPlaying(false);
          }
        } else {
          bufferTokenRef.current += 1;
          bufferPlayRef.current = null;
          try { bufferSourceRef.current?.stop(); } catch { /* already stopped */ }
          bufferSourceRef.current = null;
        }
        return;
      }
      const url = archiveBlobRef.current.primaryBlob || primarySignedUrl;
      if (!mediaPointsAt(el, url)) el.src = url;
      applyMediaPlaybackRate(el, rate);
      el.muted = muted;
      const mediaTime = Math.max(0, next - clockOffsetSec);
      if (next + 0.02 < clockOffsetSec) {
        el.pause();
        if (wantPlay) {
          softFillGap(next, clockOffsetSec, () => {
            el.currentTime = 0;
            void el.play().catch(() => {
              playingRef.current = false;
              setPlaying(false);
            });
          });
        }
        return;
      }
      el.currentTime = mediaTime;
      if (wantPlay) {
        void el.play().catch(() => {
          playingRef.current = false;
          setPlaying(false);
        });
      }
      return;
    }

    const list = playlistRef.current;
    if (!list.length) {
      if (wantPlay && next < duration - 0.05) {
        softFillGap(next, duration, () => {
          paintClock(duration, true);
          playingRef.current = false;
          setPlaying(false);
        });
      }
      return;
    }
    const ms = next * 1000;
    const first = list[0];
    const last = list[list.length - 1];
    // Before first clip: keep playhead on the session clock (leading silence).
    if (first && ms < first.startMs) {
      if (wantPlay) {
        softFillGap(next, first.startMs / 1000, () => playSegmentAt(0, 0));
      }
      return;
    }
    // After last clip: keep rolling to session end (video may still be playing).
    if (last && ms > (last.endMs || last.startMs)) {
      if (wantPlay) {
        softFillGap(next, duration, () => {
          paintClock(duration, true);
          playingRef.current = false;
          setPlaying(false);
        });
      }
      return;
    }
    let idx = 0;
    for (let i = 0; i < list.length; i += 1) {
      if (list[i].startMs <= ms) idx = i;
      else break;
    }
    const seg = list[idx];
    const nxt = list[idx + 1];
    // Gap between clips
    if (nxt && ms > (seg.endMs || seg.startMs) && ms < nxt.startMs) {
      if (wantPlay) {
        softFillGap(next, nxt.startMs / 1000, () => playSegmentAt(idx + 1, 0));
      }
      return;
    }
    const offset = Math.max(0, (ms - seg.startMs) / 1000);
    if (wantPlay) playSegmentAt(idx, offset);
    else {
      segIndexRef.current = idx;
      if (el.src !== seg.audioUrl) el.src = seg.audioUrl;
      seekingRef.current = true;
      const onMeta = () => {
        el.currentTime = offset;
        seekingRef.current = false;
        el.removeEventListener("loadedmetadata", onMeta);
      };
      if (el.readyState >= 1) {
        el.currentTime = offset;
        seekingRef.current = false;
      } else {
        el.addEventListener("loadedmetadata", onMeta);
        el.load();
      }
    }
  }, [detail, duration, hasTimeline, muted, clockSource, clockOffsetSec, paintClock, playSegmentAt, rate, softFillGap, primarySignedUrl, startTimelineBuffer]);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (!playing) {
      el.pause();
      clearGap();
      bufferTokenRef.current += 1;
      bufferPlayRef.current = null;
      try { bufferSourceRef.current?.stop(); } catch { /* already stopped */ }
      bufferSourceRef.current = null;
      return;
    }
    if (bufferPlayRef.current) return;
    if (hasTimeline && primarySignedUrl) {
      let cancelled = false;
      void (async () => {
        const buf = await ensureTimelineBuffer(primarySignedUrl);
        if (secondaryArchiveUrl) await ensureSecondaryBlob(secondaryArchiveUrl);
        if (cancelled || !playingRef.current) return;
        if (rateRef.current === 1 && buf && startTimelineBuffer(currentRef.current)) return;
        // Fallback: HTMLAudioElement + blob/signed URL
        const node = audioRef.current;
        if (!node) return;
        const url = archiveBlobRef.current.primaryBlob || primarySignedUrl;
        if (!mediaPointsAt(node, url)) {
          node.src = url;
          node.currentTime = Math.max(0, currentRef.current - clockOffsetSecRef.current);
        }
        applyMediaPlaybackRate(node, rateRef.current);
        node.muted = muted;
        const cur = currentRef.current;
        const offset = clockOffsetSecRef.current;
        if (cur + 0.02 < offset) {
          softFillGap(cur, offset, () => {
            node.currentTime = 0;
            void node.play().catch(() => setPlaying(false));
          });
          return;
        }
        void node.play().catch(() => setPlaying(false));
      })();
      return () => { cancelled = true; };
    }
    if (hasSegments) {
      const list = playlistRef.current;
      const ms = current * 1000;
      const first = list[0];
      const last = list[list.length - 1];
      if (first && ms < first.startMs) {
        softFillGap(current, first.startMs / 1000, () => playSegmentAt(0, 0));
        return;
      }
      if (last && ms > (last.endMs || last.startMs)) {
        softFillGap(current, durationRef.current, () => {
          paintClock(durationRef.current, true);
          setPlaying(false);
        });
        return;
      }
      let idx = 0;
      for (let i = 0; i < list.length; i += 1) {
        if (list[i].startMs <= ms) idx = i;
        else break;
      }
      const seg = list[idx];
      const nxt = list[idx + 1];
      if (nxt && ms > (seg.endMs || seg.startMs) && ms < nxt.startMs) {
        softFillGap(current, nxt.startMs / 1000, () => playSegmentAt(idx + 1, 0));
        return;
      }
      if (el.paused || !el.src) {
        const offset = Math.max(0, (ms - seg.startMs) / 1000);
        playSegmentAt(idx, offset);
      } else {
        void el.play().catch(() => setPlaying(false));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to play toggle
  }, [playing]);

  const onAudioTimeUpdate = (e: SyntheticEvent<HTMLAudioElement>) => {
    if (e.currentTarget !== audioRef.current) return;
    // Web Audio timeline clock owns the playhead — ignore HTML element ticks.
    if (bufferPlayRef.current?.index === -1) return;
    const el = e.currentTarget;
    if (seekingRef.current) return;
    if (hasTimeline) {
      paintClock(el.currentTime + clockOffsetSec);
      return;
    }
    const list = playlistRef.current;
    const seg = list[segIndexRef.current];
    if (!seg) return;
    paintClock((seg.startMs + el.currentTime * 1000) / 1000);
  };

  const onAudioEnded = (e: SyntheticEvent<HTMLAudioElement>) => {
    if (e.currentTarget !== audioRef.current) return;
    if (hasTimeline) {
      setPlaying(false);
      return;
    }
    const list = playlistRef.current;
    const idx = segIndexRef.current;
    if (idx + 1 >= list.length) {
      setPlaying(false);
      return;
    }
    afterSegmentRef.current(idx);
  };

  useEffect(() => {
    if (!playing || (!hasTimeline && !hasSegments)) return;
    // setInterval (not rAF): when the tab is hidden, rAF freezes and currentRef
    // stalls while secondary/video sync keeps seeking → audible loop / quiet A/V.
    const tick = () => {
      if (!playingRef.current) return;
      const ctx = audioCtxRef.current;
      if (ctx?.state === "suspended") {
        void ctx.resume().catch(() => { /* policy */ });
      }
      const sec = readPlaybackClockSec();
      if (sec != null && !seekingRef.current) paintClock(sec);
    };
    const id = window.setInterval(tick, 50);
    tick();
    return () => window.clearInterval(id);
  }, [playing, hasTimeline, hasSegments, paintClock, readPlaybackClockSec]);

  useEffect(() => {
    if (!playing || hasTimeline || hasSegments) return;
    const started = performance.now();
    const base = currentRef.current;
    const tick = () => {
      if (!playingRef.current) return;
      const elapsed = (performance.now() - started) / 1000;
      const next = base + elapsed;
      paintClock(next >= duration ? 0 : next);
    };
    const id = window.setInterval(tick, 50);
    return () => window.clearInterval(id);
  }, [playing, duration, hasTimeline, hasSegments, paintClock]);

  // Resume Web Audio + resync media when the user returns to this tab mid-play.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible" || !playingRef.current) return;
      const ctx = audioCtxRef.current;
      if (ctx?.state === "suspended") void ctx.resume().catch(() => { /* policy */ });
      const sec = readPlaybackClockSec();
      if (sec != null) paintClock(sec, true);
      const el = audioRef.current;
      if (el && el.paused && (el.currentSrc || el.src)) {
        void el.play().catch(() => { /* autoplay */ });
      }
      const secondary = secondaryAudioRef.current;
      if (secondary && secondary.paused && (secondary.currentSrc || secondary.src)) {
        void secondary.play().catch(() => { /* autoplay */ });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [paintClock, readPlaybackClockSec]);

  useEffect(() => () => clearGap(), []);

  const jumpTurn = (dir: -1 | 1) => {
    if (!transcript.length) {
      seekTo(current + dir * 5, true);
      return;
    }
    if (dir < 0) {
      const cur = transcript[activeIndex];
      if (cur && current - cur.at > 1.0) {
        seekTo(cur.at, true);
        return;
      }
      const prev = [...transcript].reverse().find((t) => t.at < current - 0.15);
      seekTo(prev ? prev.at : 0, true);
      return;
    }
    const next = transcript.find((t) => t.at > current + 0.35);
    seekTo(next ? next.at : duration, true);
  };

  const perception = detail?.perceptionAnalysis as Record<string, unknown> | null;
  const userLabel = detail ? displayUserName(detail) : id;
  const userAvatarGradient = detail
    ? avatarGradientForUserKey(sessionUserAvatarKey(detail))
    : avatarGradientForUserKey(id);
  const display = detail?.displayStatus || toDisplayStatus(detail?.status);
  const mood = moodLabel(detail?.perceptionMood || (typeof perception?.mood === "string" ? perception.mood : null));
  const affectLabels = (() => {
    const raw = Array.isArray(perception?.affect_labels)
      ? (perception?.affect_labels as string[])
      : String(perception?.emotional_state || "")
          .split(",")
          .map((s) => s.trim())
          .filter((s) => s && !s.toLowerCase().startsWith("unknown"));
    // Show emotion only when it changes: neutral → happy → sad → neutral
    const out: string[] = [];
    for (const lab of raw) {
      const cur = String(lab).toLowerCase();
      if (out.length && out[out.length - 1].toLowerCase() === cur) continue;
      out.push(String(lab));
    }
    return out;
  })();
  const scores = affectScore(affectLabels);
  const engagementRaw = detail?.emotionEngagement != null
    ? Number(detail.emotionEngagement)
    : (detail?.emotionSession?.metrics?.engagement != null
      ? Number(detail.emotionSession.metrics.engagement)
      : null);
  const engagement = engagementRaw != null && Number.isFinite(engagementRaw)
    ? Math.round(engagementRaw)
    : null;
  const engagementDisplay = engagement != null ? String(engagement) : "—";
  const commIssues = communicationIssuesScore(detail, affectLabels);
  const activeAffect = moments.find((m) => m.at <= current && (moments.find((n) => n.at > m.at && n.at <= current) == null))
    || [...moments].reverse().find((m) => m.at <= current)
    || moments[0];
  const costLabel = detail?.costUsd != null ? `$${Number(detail.costUsd).toFixed(2)}` : "—";
  const costLines = Array.isArray(detail?.costBreakdown?.lines) ? detail!.costBreakdown!.lines! : [];
  const oneMinLines = Array.isArray(detail?.oneMinuteCost?.lines) ? detail!.oneMinuteCost!.lines! : [];
  const oneMinTotal = detail?.oneMinuteCost?.totalUsd != null
    ? Number(detail.oneMinuteCost.totalUsd)
    : (oneMinLines.length
      ? oneMinLines.reduce((s, row) => s + (Number(row.usd) || 0), 0)
      : null);
  const oneMinLabel = oneMinTotal != null && Number.isFinite(oneMinTotal)
    ? `$${oneMinTotal.toFixed(2)}/min`
    : "—";

  useEffect(() => {
    if (!costOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (costRef.current && !costRef.current.contains(e.target as Node)) setCostOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCostOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [costOpen]);

  useLayoutEffect(() => {
    if (!costOpen) {
      setCostPos(null);
      return;
    }
    const place = () => {
      const btn = costBtnRef.current;
      const pop = costPopoverRef.current;
      if (!btn || !pop) return;
      const br = btn.getBoundingClientRect();
      const pad = 8;
      const gap = 8;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pw = Math.min(360, Math.max(280, pop.offsetWidth || 300));
      const preferBelow = vh - br.bottom - gap - pad;
      const preferAbove = br.top - gap - pad;
      const placeBelow = preferBelow >= 160 || preferBelow >= preferAbove;
      const maxHeight = Math.max(140, Math.min(placeBelow ? preferBelow : preferAbove, vh * 0.7));
      // Measure after maxHeight applied so height is accurate for vertical flip.
      pop.style.maxHeight = `${maxHeight}px`;
      const ph = pop.offsetHeight || Math.min(240, maxHeight);
      let left = br.left;
      if (left + pw > vw - pad) left = vw - pad - pw;
      if (left < pad) left = pad;
      let top = placeBelow ? br.bottom + gap : br.top - gap - ph;
      if (top < pad) top = pad;
      if (top + ph > vh - pad) top = Math.max(pad, vh - pad - ph);
      setCostPos((prev) => {
        if (
          prev
          && Math.abs(prev.top - top) < 0.5
          && Math.abs(prev.left - left) < 0.5
          && prev.maxHeight === maxHeight
        ) {
          return prev;
        }
        return { top, left, maxHeight };
      });
    };
    place();
    const raf = window.requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [costOpen, costLines.length, oneMinLines.length, costLabel, oneMinLabel]);
  const isFlagged = display === "Review" || String(detail?.status || "").toLowerCase() === "review";
  const sessionError = (detail?.statusReason || "").trim();
  const abandonNote = (detail?.abandonReason || "").trim();
  const abandonLabel = abandonNote
    ? abandonNote.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
    : "";
  const endReasonLabel = formatEndReason(detail?.endReason);
  const groq429 = Number(detail?.groq429Count) || 0;
  const providerDegraded = Boolean(detail?.providerDegraded) || groq429 > 0;
  const lastProviderError = (detail?.lastProviderError || "").trim();
  const lastProviderErrorAt = detail?.lastProviderErrorAt
    ? new Date(detail.lastProviderErrorAt).toLocaleString()
    : null;
  const clientAudioIssue = (detail?.clientAudioIssue || "").trim();
  const clientAudioIssueAt = detail?.clientAudioIssueAt
    ? new Date(detail.clientAudioIssueAt).toLocaleString()
    : null;
  const videoPerc = detail?.videoPerception || null;
  const videoStatus = String(detail?.videoPerceptionStatus || videoPerc?.status || "").toLowerCase();
  const passInFlight = perceptionPassInFlight(videoStatus) || rerunBusy;
  // Appearance / environment / movement come only from offline video perception —
  // never from finalize Phase-0 placeholder strings on perceptionAnalysis.
  const appearanceBody = videoPerc?.appearance
    || videoPerc?.summary
    || (videoStatus === "failed"
      ? "Video perception failed — voice/language signals are unchanged."
      : videoStatus === "running" || videoStatus === "pending_video"
        ? "Video recorded — Nova analysis pending."
        : videoMissing
          ? "Camera recording was not stored for this session."
          : detail?.userVideoUrl
            ? "User video archived. Appearance analysis not ready yet."
            : "No camera recording for this session.");
  const environmentBody = videoPerc?.environment
    || (videoStatus === "failed"
      ? "Video perception failed."
      : videoStatus === "running" || videoStatus === "pending_video" || detail?.userVideoUrl
        ? "Environment analysis pending."
        : "No camera recording for this session.");
  const movementBody = videoPerc?.movement
    || (videoStatus === "failed"
      ? "Video perception failed."
      : videoStatus === "running" || videoStatus === "pending_video" || detail?.userVideoUrl
        ? "Movement analysis pending."
        : "No camera recording for this session.");
  const signalsBody = videoPerc?.signals
    || videoPerc?.summary
    || (videoStatus === "running" || videoStatus === "pending_video"
      ? "Signal narrative pending with video analysis."
      : "");

  const fusion = detail?.perceptionFusion || null;
  const channelLines = (() => {
    if (!fusion) return null;
    const rawLabels = Array.isArray(perception?.affect_labels) ? (perception?.affect_labels as string[]) : [];
    const concern = rawLabels.filter((l) => ["anger", "frustration", "fear", "sadness"].includes(String(l).toLowerCase())).length;
    const language = fusion.channels.language === "ready"
      ? `${mood}${concern ? ` · ${concern} concern ${concern === 1 ? "turn" : "turns"}` : ""}`
      : fusion.channels.language === "pending" ? "Analysis pending" : "No language signals";
    const voiceRows = Array.isArray(perception?.voice) ? (perception?.voice as Array<Record<string, unknown>>) : [];
    const values = voiceRows.map((v) => Number(v.valence)).filter((n) => Number.isFinite(n));
    const ser = voiceRows.some((v) => {
      const model = String(v.model || "none");
      return model !== "none" && !model.startsWith("text_map");
    });
    const voiceNote = voiceRows.length
      ? `${voiceRows.length} turns · ${ser ? "SER valence" : "acoustic only"}`
      : fusion.channels.voice === "pending" ? "Analysis pending" : "No voice signals";
    let video = "No camera recording";
    if (fusion.channels.video === "ready") {
      video = String(videoPerc?.summary || "").split(/(?<=[.!?])\s/)[0] || "Analysis complete";
    } else if (fusion.channels.video === "pending") {
      video = "Analysis pending";
    } else if (fusion.channels.video === "failed") {
      video = videoStatus === "no_video"
        ? "Recording not available"
        : `Analysis failed${detail?.videoPerceptionError ? `: ${String(detail.videoPerceptionError).slice(0, 90)}` : ""}`;
    }
    return { language, voice: { values, note: voiceNote }, video };
  })();

  const videoModalContext = useMemo(
    () => buildVideoModalContext({
      atSec: current,
      durationSec: duration,
      transcriptRows,
      moments: displayMoments,
      perception,
      videoSignals: signalsBody,
      videoAppearance: appearanceBody,
      cameraLive: videoLive || botVideoLive,
    }),
    [
      current,
      duration,
      transcriptRows,
      displayMoments,
      perception,
      signalsBody,
      appearanceBody,
      videoLive,
      botVideoLive,
    ],
  );

  async function flagReview() {
    if (!id || flagBusy) return;
    setFlagBusy(true);
    try {
      await api.flagSession(id);
      setDetail((d) => (d ? { ...d, status: "review", displayStatus: "Review" } : d));
    } catch (e) {
      console.error(e);
    } finally {
      setFlagBusy(false);
    }
  }

  function exportReport() {
    if (!detail || exportBusy) return;
    setActionsOpen(false);
    setExportBusy(true);
    try {
      downloadSessionReportPdf(detail, { includeCost: canViewFinance });
    } finally {
      window.setTimeout(() => setExportBusy(false), 400);
    }
  }

  async function deleteSessionConfirmed() {
    if (!id || deleteBusy) return;
    setDeleteBusy(true);
    stopAllPlayback();
    try {
      await api.deleteSession(id);
      setDeleteOpen(false);
      navigate("/sessions");
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteBusy(false);
    }
  }

  async function removeVideoConfirmed() {
    if (!id || removeVideoBusy || !canRemoveVideo) return;
    setRemoveVideoBusy(true);
    setVideoOpen(false);
    try {
      await api.removeSessionVideo(id);
      setRemoveVideoOpen(false);
      const next = await api.getSession(id);
      // Intentional full replace after delete — media objects are gone.
      setDetail(next);
      setVideoReady(false);
      setVideoFrameReady(false);
      setVideoMissing(false);
      setBotVideoReady(false);
      setBotVideoMissing(false);
    } catch (e) {
      console.error(e);
    } finally {
      setRemoveVideoBusy(false);
    }
  }

  function openRerunPerception() {
    setActionsOpen(false);
    if (videoRemoved) return;
    if (perceptionPassInFlight(videoStatus)) {
      setRerunPhase("blocked");
      setRerunError(null);
      setRerunOpen(true);
      return;
    }
    setRerunPhase("confirm");
    setRerunError(null);
    setRerunOpen(true);
  }

  async function rerunPerceptionConfirmed() {
    if (!id || rerunBusy || videoRemoved) return;
    setRerunBusy(true);
    setRerunError(null);
    try {
      await api.rerunSessionPerception(id);
      setDetail((d) => (d ? { ...d, videoPerceptionStatus: "pending_video" } : d));
      setRerunPhase("queued");
      setToast("Video perception queued. Notes refresh when this pass finishes.");
      window.setTimeout(() => setToast(null), 4000);
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      const message = e instanceof Error ? e.message : "Could not queue perception.";
      if (status === 409 && /already running/i.test(message)) {
        setRerunPhase("blocked");
        setDetail((d) => (d ? { ...d, videoPerceptionStatus: d.videoPerceptionStatus || "pending_video" } : d));
      } else {
        setRerunPhase("error");
        setRerunError(message);
      }
    } finally {
      setRerunBusy(false);
    }
  }

  function cycleRate() {
    const i = RATES.indexOf(rate as typeof RATES[number]);
    setRate(RATES[(i + 1) % RATES.length]);
  }

  if (loadState === "loading") {
    return (
      <div className="session-detail-page">
        <SessionDetailToolbar />
        <section className="card" style={{ padding: "28px 18px", marginBottom: 14 }}>
          <div className="empty-state" style={{ padding: "48px 20px" }}>Loading session…</div>
        </section>
      </div>
    );
  }

  if (loadState === "error" || !detail) {
    return (
      <div className="session-detail-page">
        <SessionDetailToolbar />
        <section className="card" style={{ padding: "12px 18px", marginBottom: 14, borderColor: "#f0c9ce", background: "#fff7f8" }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span className="session-detail-banner-label error">Error</span>
            <span className="session-detail-banner-copy">{loadError || "Session not found"}</span>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="session-detail-page">
    <SessionDetailToolbar
      actions={<>
        <button className="btn danger" disabled={flagBusy || isFlagged} onClick={() => void flagReview()}>
          <Flag />{isFlagged ? "Flagged" : "Flag review"}
        </button>
        <div className="row-action-wrap">
          <button
            type="button"
            className="btn"
            aria-label="Session actions"
            aria-expanded={actionsOpen}
            disabled={exportBusy || deleteBusy || removeVideoBusy}
            onClick={() => setActionsOpen((v) => !v)}
          >
            <MoreHorizontal />
          </button>
          {actionsOpen && (
            <div className="row-menu" role="menu">
              {canExportReport ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={exportBusy}
                  onClick={() => exportReport()}
                >
                  <Download size={13} /> Export report
                </button>
              ) : null}
              <button
                type="button"
                role="menuitem"
                disabled={passInFlight || videoRemoved || (!userVideoUrl && !detail.recordingKeys?.userVideo)}
                title={
                  videoRemoved
                    ? "Camera archive was removed"
                    : passInFlight
                      ? "A perception pass is already running"
                      : "Run video perception again"
                }
                onClick={() => openRerunPerception()}
              >
                <RefreshCw size={13} /> Re-run perception
              </button>
              {canRemoveVideo || videoRemoved ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={!canRemoveVideo || removeVideoBusy}
                  onClick={() => { setActionsOpen(false); setRemoveVideoOpen(true); }}
                >
                  <VideoOff size={13} /> {videoRemoved ? "Video removed" : "Remove video"}
                </button>
              ) : null}
              <div className="row-menu-sep" />
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={() => { setActionsOpen(false); setDeleteOpen(true); }}
              >
                <Trash2 size={13} /> Delete session
              </button>
            </div>
          )}
        </div>
      </>}
    />
    <section className="card session-detail-hero" style={{ padding: "15px 18px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "center" }}>
        <div className="detail-user">
          <span
            className="avatar"
            style={userPortrait ? undefined : { background: userAvatarGradient }}
          >
            {userPortrait ? <img src={userPortrait} alt="" /> : initialsFrom(userLabel)}
          </span>
          <span>
            <b className="session-detail-user-name">{userLabel}</b>
            <span className="muted session-detail-user-meta">{detail?.userEmail || id} · {detail?.transport || "livekit"}</span>
          </span>
        </div>
        <div className="detail-meta">
          <span>Session started<b>{detail?.createdAt ? new Date(detail.createdAt).toLocaleString() : "—"}</b></span>
          <span>Duration<b>{time(duration)}</b></span>
          <span>
            Agent
            <b>
              {detail?.agentId ? (
                <Link
                  className="detail-agent-link"
                  href={
                    detail.agentVersion != null && Number(detail.agentVersion) > 0
                      ? `/agents?edit=${encodeURIComponent(detail.agentId)}&version=${Number(detail.agentVersion)}`
                      : `/agents?edit=${encodeURIComponent(detail.agentId)}`
                  }
                >
                  {detail.agentName || detail.agentId}
                  {detail.agentVersion != null && Number(detail.agentVersion) > 0
                    ? ` · v${Number(detail.agentVersion)}`
                    : detail.agentPublished === false
                      ? " · draft"
                      : ""}
                </Link>
              ) : (
                "default"
              )}
            </b>
          </span>
          {detail?.videoAvatarEnabled ? (
            <span>
              Avatar audio
              <b>
                {detail.avatarAudioPath === "tts"
                  ? "TTS only"
                  : detail.avatarAudioPath === "liveavatar"
                    ? "LiveAvatar only"
                    : "Auto"}
                {detail.liveAvatarSandbox ? " · sandbox" : ""}
              </b>
            </span>
          ) : null}
          {canViewFinance ? (
          <span className="detail-cost" ref={costRef}>
            <button
              ref={costBtnRef}
              type="button"
              className={`detail-cost-btn${costOpen ? " open" : ""}`}
              disabled={detail?.costUsd == null && !costLines.length && !oneMinLines.length}
              onClick={() => setCostOpen((v) => !v)}
              aria-expanded={costOpen}
              aria-haspopup="dialog"
            >
              Cost ($)<b>{costLabel}</b>
              {oneMinLabel !== "—" ? <em className="detail-cost-rate">{oneMinLabel}</em> : null}
            </button>
            {costOpen ? (
              <div
                ref={costPopoverRef}
                className="cost-popover cost-popover--fixed"
                role="dialog"
                aria-label="Session cost breakdown"
                style={
                  costPos
                    ? { top: costPos.top, left: costPos.left, maxHeight: costPos.maxHeight }
                    : { visibility: "hidden" }
                }
              >
                <header>
                  <b>Session total</b>
                  <span>{costLabel}</span>
                </header>
                {costLines.length ? (
                  <table>
                    <tbody>
                      {costLines.map((row) => (
                        <tr key={row.id}>
                          <td>
                            <span className="cost-line-label">{row.label}</span>
                            {row.detail ? <span className="cost-line-detail">{row.detail}</span> : null}
                          </td>
                          <td>${Number(row.usd).toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td>{costLabel}</td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <p className="cost-popover-empty">Usage meters not available for this session yet.</p>
                )}
                {oneMinLines.length ? (
                  <>
                    <header className="cost-popover-subheader">
                      <b>1-min rate (this profile)</b>
                      <span>{oneMinLabel}</span>
                    </header>
                    <table>
                      <tbody>
                        {oneMinLines.map((row) => (
                          <tr key={`min-${row.id}`}>
                            <td>
                              <span className="cost-line-label">{row.label}</span>
                              {row.detail ? <span className="cost-line-detail">{row.detail}</span> : null}
                            </td>
                            <td>${Number(row.usd).toFixed(4)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>Per minute</td>
                          <td>{oneMinLabel}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </>
                ) : null}
              </div>
            ) : null}
          </span>
          ) : null}
          <span>Outcome<b><Pill tone={display === "Failed" ? "red" : display === "Review" ? "orange" : display === "Live" ? "blue" : "green"}><span className="dot" />{display}</Pill></b></span>
          {endReasonLabel && display !== "Live" ? (
            <span>End reason<b><Pill tone={detail?.endReason === "session_limit" ? "orange" : "gray"}>{endReasonLabel}</Pill></b></span>
          ) : null}
          {abandonLabel ? (
            <span>Ended early<b><Pill tone="gray">{abandonLabel}</Pill></b></span>
          ) : null}
        </div>
      </div>
    </section>

    {sessionError ? (
      <section className="card" style={{ padding: "12px 18px", marginBottom: 14, borderColor: "#f0c9ce", background: "#fff7f8" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 10, fontWeight: 750, letterSpacing: 0.4, color: "#c62839", textTransform: "uppercase", whiteSpace: "nowrap", marginTop: 2 }}>Session error</span>
          <span style={{ fontSize: 11, color: "#5c1f28", lineHeight: 1.55 }}>{sessionError}</span>
        </div>
      </section>
    ) : null}

    {providerDegraded ? (
      <section className="card" style={{ padding: "12px 18px", marginBottom: 14, borderColor: "#f5d9a8", background: "#fffbf2" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 10, fontWeight: 750, letterSpacing: 0.4, color: "#b45309", textTransform: "uppercase", whiteSpace: "nowrap", marginTop: 2 }}>Provider</span>
          <span style={{ fontSize: 11, color: "#7c4a03", lineHeight: 1.55 }}>
            {detail?.degradedReason
              || (groq429 > 0
                ? `Rate limited · ${groq429} event${groq429 === 1 ? "" : "s"}`
                : "Quality path degraded")}
            {detail?.degradedReasonCode ? ` · ${detail.degradedReasonCode}` : ""}
            {lastProviderError && !detail?.degradedReason ? ` · last ${lastProviderError}` : ""}
            {lastProviderErrorAt ? ` · ${lastProviderErrorAt}` : ""}
          </span>
        </div>
      </section>
    ) : null}

    {clientAudioIssue ? (
      <section className="card" style={{ padding: "12px 18px", marginBottom: 14, borderColor: "#f5d9a8", background: "#fffbf2" }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
          <span style={{ fontSize: 10, fontWeight: 750, letterSpacing: 0.4, color: "#b45309", textTransform: "uppercase", whiteSpace: "nowrap", marginTop: 2 }}>Client audio blocked</span>
          <span style={{ fontSize: 11, color: "#7c4a03", lineHeight: 1.55 }}>
            {clientAudioIssue === "audio_blocked"
              ? "The browser blocked the agent audio (autoplay policy)"
              : "The agent audio failed to play in the browser"}
            {detail?.clientAudioIssueMessage ? ` · ${detail.clientAudioIssueMessage}` : ""}
            {clientAudioIssueAt ? ` · ${clientAudioIssueAt}` : ""}
            {detail?.clientAudioResumedAt ? " · playback resumed" : " · not resumed"}
          </span>
        </div>
      </section>
    ) : null}

    {archivePlayback.unavailable && archivePlayback.message ? (
      <section className="card session-archive-hint" role="status" aria-live="polite">
        <p>{archivePlayback.message}</p>
      </section>
    ) : null}

    <div className="detail-grid">
    <div className="detail-grid-playback">
    <section className="card player-card">
      <div className="player-top">
        <div className="player-title">
          <Volume2 size={16} />Session recording
        </div>
        <div className="signal-events">
          {String(detail?.agentMode || "").toLowerCase() !== "liveavatar" ? (
            <button type="button" className="btn" onClick={() => setPipelineOpen(true)}>
              View pipeline timeline →
            </button>
          ) : null}
          <Pill tone="purple">{moments.length} perception notes</Pill>
          <Pill
            tone={
              peaksPending
                ? "orange"
                : archivePlayback.unavailable
                  ? "gray"
                  : hasTimeline || hasSegments
                    ? "green"
                    : "gray"
            }
          >
            {peaksPending
              ? "Processing"
              : archivePlayback.unavailable
                ? "No recording"
                : hasTimeline || hasSegments
                  ? "Playable"
                  : waves.length
                    ? "Peaks only"
                    : "No audio"}
          </Pill>
          {hasUserVideo ? <Pill tone="blue">Camera</Pill> : videoMissing ? <Pill tone="gray">Camera unavailable</Pill> : null}
          {hasBotVideo ? <Pill tone="purple">Agent video</Pill> : null}
          {String(detail?.agentMode || "").toLowerCase() === "liveavatar" ? (
            <Pill tone="orange">LiveAvatar FULL</Pill>
          ) : null}
        </div>
      </div>
      <audio
        ref={(node) => {
          slotRefs.current[0] = node;
          if (activeSlotRef.current === 0) audioRef.current = node;
        }}
        preload="auto"
        onTimeUpdate={onAudioTimeUpdate}
        onEnded={onAudioEnded}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (hasTimeline && Number.isFinite(d) && d > 0) {
            setArchiveAudioDurationSec(Math.max(d, archiveAudioDurationSec));
          }
        }}
        onWaiting={() => { /* keep UI playing state; browser buffers */ }}
        onStalled={() => { /* avoid hard stop on brief stalls */ }}
      />
      <audio
        ref={(node) => {
          slotRefs.current[1] = node;
          if (activeSlotRef.current === 1) audioRef.current = node;
        }}
        preload="auto"
        hidden
        onTimeUpdate={onAudioTimeUpdate}
        onEnded={onAudioEnded}
      />
      {secondaryArchiveUrl ? (
        <audio
          ref={secondaryAudioRef}
          preload="auto"
          hidden
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (Number.isFinite(d) && d > 0) {
              setArchiveAudioDurationSec((prev) => Math.max(prev, d + secondaryOffsetSec));
            }
          }}
        />
      ) : null}
      <div className={`wave-row ${userVideoUrl || botVideoUrl || videoRemoved ? "has-video" : ""} ${userVideoUrl && botVideoUrl ? "has-video-duo" : ""}`}>
        <div
          className={`wave-stage${peaksPending ? " is-processing" : ""}`}
          onClick={(e) => {
            if (peaksPending) return;
            // Map X against the waveform element only — not wave-plot (legend/padding
            // siblings used to skew seek further toward the end of the call).
            const wave = waveformRef.current;
            const plot = (e.currentTarget as HTMLElement).querySelector(".wave-plot") as HTMLElement | null;
            const rect = (wave || plot || e.currentTarget).getBoundingClientRect();
            const next = Math.max(0, Math.min(duration, ((e.clientX - rect.left) / Math.max(1, rect.width)) * duration));
            seekTo(next);
          }}
        >
          <div className="wave-plot">
          <div className="turn-bands" aria-hidden="true">
            {playlist.map((seg, i) => {
              const spanMs = Math.max(0, seg.endMs - seg.startMs);
              if (spanMs <= 0) return null;
              const role = String(seg.role || "").toLowerCase();
              const kind = role === "user" ? "user" : "agent";
              const left = Math.max(0, Math.min(100, (seg.startMs / 1000 / duration) * 100));
              const width = Math.max(0, Math.min(100 - left, (spanMs / 1000 / duration) * 100));
              if (width <= 0) return null;
              return (
                <span
                  key={`${seg.startMs}-${i}`}
                  className={`turn-band ${kind}`}
                  style={{ left: `${left}%`, width: `${width}%` }}
                />
              );
            })}
          </div>
          <div className={`waveform${duoWave ? " is-duo" : ""}`} ref={waveformRef}>
            {duoWave ? (
              <div className="wave-lane wave-lane-agent" data-lane="agent" aria-label="Agent audio waveform">
                <div className="wave-lane-bars">
                  {botWaves.map((h, i) => (
                    <i key={`a-${i}`} className="wavebar wavebar-agent" style={{ height: h }} />
                  ))}
                </div>
                <span className="wave-lane-tag" aria-hidden="true">Agent</span>
              </div>
            ) : null}
            {waves.length === 0 ? null : (
              <div className="wave-lane wave-lane-user" data-lane="user" aria-label="User audio waveform">
                <div className="wave-lane-bars">
                  {waves.map((h, i) => (
                    <i key={`u-${i}`} className="wavebar wavebar-user" style={{ height: h }} />
                  ))}
                </div>
                {duoWave ? <span className="wave-lane-tag" aria-hidden="true">User</span> : null}
              </div>
            )}
            <span ref={playheadRef} className="playhead" style={{ left: `${(current / duration) * 100}%` }} />
          </div>
          {displayMoments.map((m) => (
            <button
              key={`emo-${m.at}-${m.label}`}
              className="moment moment-emotion"
              data-label={m.label}
              aria-label={`Emotion: ${m.label}`}
              onClick={(e) => {
                e.stopPropagation();
                seekTo(m.at, true);
              }}
              style={{ left: `${(m.at / duration) * 100}%`, background: m.color || "#0968f2" }}
            />
          ))}
          {topicMoments.map((m) => (
            <button
              key={`topic-${m.at}-${m.label}`}
              className="moment moment-topic"
              data-label={m.label}
              aria-label={`Topic: ${m.label}`}
              onClick={(e) => {
                e.stopPropagation();
                seekTo(m.at, true);
              }}
              style={{ left: `${(m.at / duration) * 100}%`, background: m.color || "#94a3b8" }}
            />
          ))}
          </div>
          {peaksPending ? (
            <div className="wave-processing-overlay" role="status" aria-live="polite">
              <span className="wave-processing-spinner" aria-hidden="true" />
              <span className="wave-processing-title">Processing recording</span>
              <span className="wave-processing-sub">
                Waveform peaks are built after hangup (usually within a few minutes). Playback controls unlock when ready.
              </span>
            </div>
          ) : null}
        </div>
        {videoRemoved && showUserPlate ? (
          <div className="camera-plate is-missing is-removed" aria-label="Camera recording deleted">
            <span className="camera-plate-empty">
              <VideoOff size={16} />
              <span>Recording deleted</span>
            </span>
          </div>
        ) : showCameraDuo ? (
          <div
            className={`camera-duo${canOpenVideoModal ? " is-openable" : ""}`}
            role="button"
            tabIndex={canOpenVideoModal ? 0 : -1}
            aria-disabled={!canOpenVideoModal}
            aria-label={
              userVideoUrl && botVideoUrl
                ? "Open agent and user cameras"
                : userVideoUrl
                  ? "Open user camera"
                  : "Open agent video"
            }
            title={
              canOpenVideoModal
                ? (userVideoUrl && botVideoUrl ? "Open cameras" : "Open camera")
                : "Loading camera recording"
            }
            onClick={() => {
              if (canOpenVideoModal) setVideoOpen(true);
            }}
            onKeyDown={(e) => {
              if (!canOpenVideoModal) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setVideoOpen(true);
              }
            }}
          >
            {botVideoUrl && botVideoMissing && showBotPlate ? (
              <div className="camera-plate is-missing bot-plate" aria-label="Agent video unavailable">
                <span className="camera-plate-empty">
                  <VideoOff size={16} />
                  Unavailable
                </span>
              </div>
            ) : botVideoUrl && showBotPlate ? (
              <div
                className={`camera-plate bot-plate${botVideoReady ? " is-framed" : " is-pending"}${botVideoReady && !botVideoLive ? " is-gap" : ""}`}
              >
                {playbackBotVideoUrl ? (
                  <div ref={botPlateHostRef} className="camera-video-host" />
                ) : null}
                {!botVideoReady ? (
                  <span className="camera-plate-loader" aria-hidden="true">
                    <span className="camera-plate-spinner" />
                  </span>
                ) : null}
              </div>
            ) : null}
            {userVideoUrl && videoMissing && showUserPlate ? (
              <div className="camera-plate is-missing" aria-label="Camera recording unavailable">
                <span className="camera-plate-empty">
                  <VideoOff size={16} />
                  Unavailable
                </span>
              </div>
            ) : userVideoUrl && showUserPlate ? (
              <div
                className={`camera-plate${videoFrameReady ? " is-framed" : " is-pending"}${videoFrameReady && !videoLive ? " is-gap" : ""}`}
              >
                {playbackUserVideoUrl ? (
                  <div ref={userPlateHostRef} className="camera-video-host" />
                ) : null}
                {!videoFrameReady ? (
                  <span className="camera-plate-loader" aria-hidden="true">
                    <span className="camera-plate-spinner" />
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      {peaksPending ? (
        <div className="player-controls is-processing" aria-busy="true">
          <span className="timecode muted">— / —</span>
          <div className="player-processing-note">
            Waiting for waveform postprocess…
          </div>
        </div>
      ) : (
      <div className="player-controls">
        <span className="timecode"><span ref={timecodeRef}>{time(current)}</span> <span className="muted">/ {time(duration)}</span></span>
        <div className="transport">
          <button className="round-control" onClick={() => jumpTurn(-1)} aria-label="Previous turn"><SkipBack /></button>
          <button
            className="round-control play"
            onClick={() => {
              const ctx = audioCtxRef.current;
              if (ctx?.state === "suspended") void ctx.resume();
              setPlaying((p) => !p);
            }}
            aria-label={playing ? "Pause" : "Play"}
          >
            {playing ? <Pause /> : <Play style={{ marginLeft: 2 }} />}
          </button>
          <button className="round-control" onClick={() => jumpTurn(1)} aria-label="Next turn"><SkipForward /></button>
        </div>
        <div className="play-options">
          <button type="button" className="play-option-btn" onClick={cycleRate} title="Playback speed">{rate}×</button>
          <button
            type="button"
            className={`play-option-btn volume-mute${muted ? " is-muted" : ""}`}
            onClick={() => setMuted((m) => !m)}
            aria-label={muted ? "Unmute" : "Mute"}
            aria-pressed={muted}
          >
            {muted ? <VolumeX /> : <Volume2 />}
          </button>
        </div>
      </div>
      )}
    </section>

    <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
      <DialogContent
        className={`session-video-dialog bg-white border-[#dce5ef] ${userVideoUrl && botVideoUrl ? "sm:max-w-[min(1120px,calc(100%-1.5rem))]" : "sm:max-w-[min(920px,calc(100%-1.5rem))]"}`}
        showCloseButton={false}
      >
        <DialogHeader className="session-video-dialog-head">
          <DialogTitle>
            {userVideoUrl && botVideoUrl ? "Cameras" : userVideoUrl ? "User camera" : "Agent video"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Session camera recordings linked to audio playback.
          </DialogDescription>
        </DialogHeader>
        <div className="session-video-dialog-body">
        <div className="session-video-modal">
            <div className={`session-video-stage${userVideoUrl && botVideoUrl ? " is-duo" : ""}`}>
              {botVideoUrl && !botVideoMissing ? (
                <div
                  className={`session-video-frame${modalBotFrameReady || botVideoReady ? " is-framed" : " is-pending"}${modalBotFrameReady && !botVideoLive ? " is-gap" : ""}`}
                  onClick={() => setPlaying((p) => !p)}
                >
                  <div ref={botModalHostRef} className="session-video-host" />
                  {!(modalBotFrameReady || botVideoReady) ? (
                    <span className="camera-plate-loader" aria-hidden="true">
                      <span className="camera-plate-spinner" />
                    </span>
                  ) : null}
                  <a
                    className="session-video-ctrl bottom-right"
                    href={botVideoUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Download agent video"
                    title="Download agent video"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download />
                  </a>
                </div>
              ) : null}
              {userVideoUrl && !videoMissing ? (
                <div
                  className={`session-video-frame${modalFrameReady || videoFrameReady ? " is-framed" : " is-pending"}${modalFrameReady && !videoLive ? " is-gap" : ""}`}
                  onClick={() => setPlaying((p) => !p)}
                >
                  <div ref={userModalHostRef} className="session-video-host" />
                  {!(modalFrameReady || videoFrameReady) ? (
                    <span className="camera-plate-loader" aria-hidden="true">
                      <span className="camera-plate-spinner" />
                    </span>
                  ) : null}
                  <a
                    className="session-video-ctrl bottom-right"
                    href={userVideoUrl}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Download user video"
                    title="Download user video"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Download />
                  </a>
                </div>
              ) : null}
              <button
                type="button"
                className="session-video-ctrl top-right"
                aria-label={playing ? "Pause audio" : "Play audio"}
                title={playing ? "Pause audio" : "Play audio"}
                onClick={(e) => {
                  e.stopPropagation();
                  setPlaying((p) => !p);
                }}
              >
                {playing ? <Pause /> : <Play />}
              </button>
            </div>
          <aside className="session-video-context" aria-live="polite">
            <div className="session-video-context-head">
              <span className="session-video-context-clock">{time(videoModalContext.atSec)}</span>
              <button type="button" className="btn" onClick={() => setVideoOpen(false)}>Close</button>
            </div>
            <div className="session-video-context-scroll">
              <section className="session-video-context-section session-video-context-section--moment">
                <h3>At this moment</h3>
                {videoModalContext.transcript.agent || videoModalContext.transcript.user ? (
                  <div className="session-video-transcript-moment">
                    {videoModalContext.transcript.agent ? (
                      <p
                        className={
                          videoModalContext.transcript.activeSpeaker === "agent"
                            ? "is-active"
                            : "is-context"
                        }
                      >
                        <b>Agent</b>
                        <span>{videoModalContext.transcript.agent}</span>
                      </p>
                    ) : null}
                    {videoModalContext.transcript.user ? (
                      <p
                        className={
                          videoModalContext.transcript.activeSpeaker === "user" ? "is-active" : ""
                        }
                      >
                        <b>User</b>
                        <span>{videoModalContext.transcript.user}</span>
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <p className="muted session-video-context-empty">No transcript near this moment.</p>
                )}
              </section>
              <div className="session-video-context-layers">
                {videoModalContext.layers.map((layer) => (
                  <section key={layer.id} className="session-video-context-section">
                    <h3>{layer.title}</h3>
                    <p>{layer.body}</p>
                    {layer.atSec != null ? (
                      <span className="session-video-context-at">{time(layer.atSec)}</span>
                    ) : null}
                  </section>
                ))}
                {!videoModalContext.layers.length ? (
                  <p className="muted session-video-context-empty">
                    Perception layers will appear when language, voice, acoustics, or camera signals exist for this call.
                  </p>
                ) : null}
              </div>
            </div>
          </aside>
        </div>
        </div>
      </DialogContent>
    </Dialog>

      <Card
        className="detail-grid-transcript"
        title="Synchronized transcript"
        kicker="Click any passage to jump to that moment"
        action={<div className="search-wrap" style={{ minWidth: 170 }}><MessageSquareText /><input className="field" value={txQuery} onChange={(e) => setTxQuery(e.target.value)} placeholder="Search transcript" /></div>}
      >
        <div className="transcript-list" ref={transcriptListRef}>
          {transcript.length === 0 && (
            <div className="empty-state">
              {transcriptRows.length > 0 && txSearch
                ? "No lines match this search."
                : "No transcript for this session."}
            </div>
          )}
          {transcript.map((row, i) => (
            <div
              key={`${row.at}-${i}`}
              ref={(el) => { rowRefs.current[i] = el; }}
              className={`transcript-row ${i === activeIndex ? "active" : ""} ${row.unrecognized ? "is-unrecognized" : ""} ${row.debug ? "is-live-perception" : ""}`}
              onClick={() => seekTo(row.at, true)}
            >
              <span className="transcript-time">{time(row.at)}</span>
              <span className={`speaker ${row.agent ? "agent" : ""} ${row.debug ? "live-perception" : ""}`}>
                {row.debug ? (
                  "LP"
                ) : row.agent && detail.agentAvatarUrl ? (
                  <img src={detail.agentAvatarUrl} alt="" />
                ) : !row.agent && userPortrait ? (
                  <img src={userPortrait} alt="" />
                ) : row.agent ? (
                  "A"
                ) : (
                  initialsFrom(userLabel)
                )}
              </span>
              <span className="utterance">
                <b>{row.speaker}</b>
                <p>{row.text}</p>
                {row.debug && row.frameUrl ? (
                  <img className="transcript-lp-frame" src={row.frameUrl} alt="" />
                ) : null}
              </span>
            </div>
          ))}
        </div>
      </Card>
    </div>
      <section className="card detail-grid-perception">
        <div className="card-head">
          <div>
            <h2 className="card-title">Perception analysis</h2>
            <span className="card-kicker">Signals from session artifacts</span>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {passInFlight ? (
              <button type="button" className="btn" disabled aria-busy="true" title="Video perception is running">
                <RefreshCw size={14} className="spin" /> Analyzing…
              </button>
            ) : (
              <Link className="btn primary" href={`/sessions/${id}/perception`}>
                Open analysis
              </Link>
            )}
            <Pill tone={displayMoments.length ? "green" : "gray"}>
              {displayMoments.length
                ? `${displayMoments.length} emotion ${displayMoments.length === 1 ? "beat" : "beats"}`
                : "No timed emotions yet"}
            </Pill>
          </div>
        </div>
        {fusion && channelLines ? (
          <PerceptionChannelsStrip
            fusion={fusion}
            language={channelLines.language}
            voice={channelLines.voice}
            video={channelLines.video}
          />
        ) : null}
        <div className="perception-tabs">{["Signals", "Appearance", "Environment", "Movement"].map((x) => <button key={x} className={tab === x ? "active" : ""} onClick={() => setTab(x)}>{x}</button>)}</div>
        <div className="card-body">
          {tab === "Signals" && <>
            <div className="mood-hero">
              <PerceptionSignalsHelp />
              <div className="mood-score-wrap">
                <div className="mood-score"><span>{engagementDisplay}</span></div>
              </div>
              <div className="mood-hero-copy">
                <span className="mood-hero-label-row">
                  <b>{activeAffect ? String(activeAffect.label) : mood}</b>
                </span>
                <span className="muted mood-hero-meta">
                  {activeAffect ? `At ${time(activeAffect.at)}` : "No playhead emotion yet"}
                  {displayMoments.length ? (
                    <>
                      {" · "}
                      {displayMoments.length} emotion {displayMoments.length === 1 ? "beat" : "beats"}
                    </>
                  ) : null}
                </span>
              </div>
            </div>
            <div style={{ marginTop: 12 }}>
              <Signal
                label="Engagement"
                value={engagement ?? scores.engagement}
                color="#0968f2"
                help={SESSION_SIGNAL_HELP.engagement}
              />
              <Signal label="Valence" value={scores.valence} color="#0aa878" help={SESSION_SIGNAL_HELP.valence} />
              <Signal label="Arousal" value={scores.arousal} color="#8057e8" help={SESSION_SIGNAL_HELP.arousal} />
              <Signal
                label="Conversation comfort"
                value={Math.min(96, Math.round(((engagement ?? scores.engagement) + scores.valence) / 2))}
                color="#29a5c9"
                help={SESSION_SIGNAL_HELP.comfort}
              />
              <Signal label="Communication issues" value={commIssues} color="#111111" help={SESSION_SIGNAL_HELP.communicationIssues} />
            </div>
            <div className="mood-chip-row">
              {displayMoments.length === 0 && <span className="muted" style={{ fontSize: 10 }}>No timed emotion beats on this session yet.</span>}
              {displayMoments.map((m, i) => {
                const chipStyle = moodMomentChipStyle(m.label, m.color);
                return (
                  <button
                    key={`${m.at}-${m.label}-${i}`}
                    type="button"
                    className="pill mood-moment-chip"
                    style={{
                      color: chipStyle.color,
                      background: chipStyle.background,
                      borderColor: chipStyle.borderColor,
                    }}
                    onClick={() => seekTo(m.at, true)}
                  >
                    {time(m.at)} · {m.label}
                  </button>
                );
              })}
            </div>
            {(signalsBody || notesFor(videoPerc, "signals").length) ? (
              <div style={{ marginTop: 16 }}>
                <PerceptionInsight
                  icon="☉"
                  title="Observable signals"
                  body={signalsBody || "No signal narrative yet."}
                  bullets={notesFor(videoPerc, "signals")}
                  facts={observationFacts(videoPerc)}
                />
              </div>
            ) : null}
          </>}
          {tab === "Appearance" && (
            <PerceptionInsight
              icon="◎"
              title="Presentation"
              body={appearanceBody}
              bullets={notesFor(videoPerc, "appearance")}
              facts={observationFacts(videoPerc).filter((f) => /face|smile|gaze|quality|clothing/i.test(f))}
            />
          )}
          {tab === "Environment" && (
            <PerceptionInsight
              icon="⌂"
              title="Environment"
              body={environmentBody}
              bullets={notesFor(videoPerc, "environment")}
              facts={observationFacts(videoPerc).filter((f) => /people|light|scene|room/i.test(f))}
            />
          )}
          {tab === "Movement" && (
            <PerceptionInsight
              icon="↗"
              title="Movement"
              body={movementBody}
              bullets={notesFor(videoPerc, "movement")}
              facts={observationFacts(videoPerc).filter((f) => /movement|lean|frame|posture|gestur/i.test(f))}
            />
          )}
        </div>
      </section>
    </div>
    {pipelineOpen && detail && String(detail.agentMode || "").toLowerCase() !== "liveavatar" ? (
      <PipelineTimelineModal detail={detail} onClose={() => setPipelineOpen(false)} />
    ) : null}
    <Dialog open={deleteOpen} onOpenChange={(v) => { if (!v && !deleteBusy) setDeleteOpen(false); }}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Delete session?</DialogTitle>
          <DialogDescription>
            This permanently removes session metadata and recording artifacts for{" "}
            <b>{userLabel}</b>
            {detail && displayTopic(detail) !== "—" ? <> · <b>{displayTopic(detail)}</b></> : null}
            . This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button className="btn" onClick={() => setDeleteOpen(false)} disabled={deleteBusy}>Cancel</button>
          <button className="btn danger" onClick={() => void deleteSessionConfirmed()} disabled={deleteBusy}>
            <Trash2 size={14} />{deleteBusy ? "Deleting…" : "Delete session"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={removeVideoOpen} onOpenChange={(v) => { if (!v && !removeVideoBusy) setRemoveVideoOpen(false); }}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Remove video?</DialogTitle>
          <DialogDescription>
            Permanently deletes camera and agent video archives for this session. Audio, transcript,
            and perception analysis stay. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button className="btn" onClick={() => setRemoveVideoOpen(false)} disabled={removeVideoBusy}>Cancel</button>
          <button className="btn danger" onClick={() => void removeVideoConfirmed()} disabled={removeVideoBusy}>
            <VideoOff size={14} />{removeVideoBusy ? "Removing…" : "Remove video"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={rerunOpen} onOpenChange={(v) => { if (!v && !rerunBusy) setRerunOpen(false); }}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {rerunPhase === "blocked"
              ? "Perception already running"
              : rerunPhase === "queued"
                ? "Perception queued"
                : rerunPhase === "error"
                  ? "Could not start perception"
                  : "Re-run perception?"}
          </DialogTitle>
          <DialogDescription>
            {rerunPhase === "blocked"
              ? "A video perception pass is already in progress for this session. Wait until it finishes (Analyzing… replaces Open analysis). You cannot queue another pass until then."
              : rerunPhase === "queued"
                ? "A new video analysis is queued. Current Appearance / Environment / Movement notes stay until this pass finishes, usually a few minutes. Open analysis comes back when it completes."
                : rerunPhase === "error"
                  ? (rerunError || "The perception job could not be queued.")
                  : "Queues a new video analysis. Current notes stay until the new pass finishes, usually a few minutes. You cannot start another pass while this one is running."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {rerunPhase === "confirm" ? (
            <>
              <button className="btn" onClick={() => setRerunOpen(false)} disabled={rerunBusy}>Cancel</button>
              <button className="btn primary" onClick={() => void rerunPerceptionConfirmed()} disabled={rerunBusy}>
                <RefreshCw size={14} />{rerunBusy ? "Queueing…" : "Re-run perception"}
              </button>
            </>
          ) : (
            <button className="btn primary" onClick={() => setRerunOpen(false)} disabled={rerunBusy}>Close</button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {toast ? (
      <div className="toast" role="status">
        <span className="toast-icon">✓</span>
        <span><b>{toast}</b></span>
      </div>
    ) : null}
  </div>
  );
}

function Signal({ label, value, color, help }: { label: string; value: number; color: string; help: string }) {
  return (
    <div className="signal-row">
      <div className="signal-head">
        <span className="signal-label">
          {label}
          <SessionMetricHelpPopover
            wrapClassName="signal-metric-help"
            iconSize={13}
            ariaLabel={`About ${label}`}
            dialogTitle={label}
            sections={[{ title: label, body: help }]}
          />
        </span>
        <b>{value}%</b>
      </div>
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${value}%`, background: color }} />
      </div>
    </div>
  );
}
function notesFor(
  perc: { notes?: { signals?: string[]; appearance?: string[]; environment?: string[]; movement?: string[] } } | null,
  key: "signals" | "appearance" | "environment" | "movement",
): string[] {
  const arr = perc?.notes?.[key];
  return Array.isArray(arr) ? arr.map((x) => String(x || "").trim()).filter(Boolean) : [];
}

function observationFacts(perc: { observations?: unknown; quality?: unknown } | null): string[] {
  const out: string[] = [];
  const obs = perc?.observations && typeof perc.observations === "object"
    ? perc.observations as Record<string, Record<string, unknown>>
    : {};
  const face = obs.face || {};
  const body = obs.body || {};
  const env = obs.environment || {};
  const quality = perc?.quality && typeof perc.quality === "object"
    ? perc.quality as Record<string, unknown>
    : {};
  const pct = (v: unknown) => {
    const n = Number(v);
    return Number.isFinite(n) ? `${Math.round(n * 100)}%` : null;
  };
  if (pct(face.visible) || pct(quality.faceVisibility)) {
    out.push(`Face visible ${pct(face.visible) || pct(quality.faceVisibility)}`);
  }
  if (pct(face.gazeAway)) out.push(`Gaze away ${pct(face.gazeAway)}`);
  if (pct(face.visibleTension)) out.push(`Visible tension ${pct(face.visibleTension)}`);
  if (pct(face.sustainedSmile)) out.push(`Smile ${pct(face.sustainedSmile)}`);
  if (pct(body.movementIntensity)) out.push(`Movement ${pct(body.movementIntensity)}`);
  if (pct(body.leanForward)) out.push(`Lean forward ${pct(body.leanForward)}`);
  if (pct(body.leftFrame)) out.push(`Left frame ${pct(body.leftFrame)}`);
  if (env.peopleCount != null && env.peopleCount !== "") out.push(`People in view ${Number(env.peopleCount)}`);
  if (pct(env.lightingChanged)) out.push(`Lighting change ${pct(env.lightingChanged)}`);
  if (pct(quality.lighting)) out.push(`Lighting quality ${pct(quality.lighting)}`);
  return out;
}

function PerceptionInsight({
  icon,
  title,
  body,
  bullets,
  facts,
}: {
  icon: string;
  title: string;
  body: string;
  bullets?: string[];
  facts?: string[];
}) {
  const paras = String(body || "")
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const bodyText = paras.join("\n\n");
  const extraBullets = insightBulletsBeyondBody(bodyText, bullets || []);
  const metricFacts = (facts || []).map((f) => parseObservationFact(f));

  return (
    <article className="perception-insight">
      <header className="perception-insight-head">
        <span className="perception-insight-icon" aria-hidden="true">{icon}</span>
        <h3 className="perception-insight-title">{title}</h3>
      </header>

      {paras.length > 0 ? (
        <div className="perception-insight-summary">
          {paras.map((p, i) => (
            <p key={`${i}-${p.slice(0, 20)}`}>{p}</p>
          ))}
        </div>
      ) : (
        <p className="perception-insight-empty muted">No analysis text for this section yet.</p>
      )}

      {metricFacts.length > 0 ? (
        <section className="perception-insight-metrics" aria-label="Observation metrics">
          <span className="perception-insight-kicker">Across the recording</span>
          <dl className="perception-insight-metric-grid">
            {metricFacts.map((m) => (
              <div key={m.label} className="perception-insight-metric">
                <dt>{m.label}</dt>
                {m.value ? <dd>{m.value}</dd> : null}
              </div>
            ))}
          </dl>
        </section>
      ) : null}

      {extraBullets.length > 0 ? (
        <section className="perception-insight-highlights">
          <span className="perception-insight-kicker">Additional notes</span>
          <ul>
            {extraBullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </article>
  );
}
